import Phaser from 'phaser';

import type {
  CharacterRendererSpec,
  ICharacterRenderer,
  LocomotionIntent,
  LocomotionState,
  LocomotionTuning,
  Ride,
  RideArt,
  RigArtboard,
  RigDocument,
  ThemeColours,
  Vec2,
} from '@application/ports';

import { blendColors, mixColor, toCssColor, toPhaserColor } from './boot-config';
import { backingScaleOf, fitCameraToDesign } from './design-viewport';
import { groundFillFloor } from './ground-dressing';
import { groundYAt, levelBounds, slopeAt, type LevelBounds } from './ground-profile';
import type { LoadRequest } from './level-assets';
import {
  PLACEHOLDER_BAND_HEIGHT,
  createLevelEffects,
  requestedParticlesFor,
  selectLayers,
  type LayerViewport,
  type LevelEffects,
} from './level-effects';
import type {
  SceneEventListener,
  SceneEventName,
  SceneMilestoneListener,
  SceneMilestoneName,
} from './level-events';
import {
  artboardFor,
  castGapMessage,
  drivableInputs,
  playerArtboard,
  rigAtlasKey,
  unboundAnimationInputs,
  type CastGap,
} from './character-cast';
import {
  brakingIntent,
  brakingTuning,
  createAutoStop,
  type AutoStopSubject,
  type AutoStopWatch,
} from './auto-stop';
import {
  MARK_RING_FRACTION,
  MARK_TIP_FRACTION,
  affordanceMarks,
  markAnchor,
  markExtent,
  markPulse,
  type AffordanceMark,
  type AffordanceSubject,
} from './interaction-affordance';
import { placeSilhouette, silhouetteBounds, silhouetteFromRgba, type ArtSilhouette } from './art-silhouette';
import { figureSilhouette, type FigureBox } from './figure-extent';
import { markClearance, playerStandingFigure, restingHeadBoxes, walkingFigureBoxes } from './mark-clearance';
import {
  MAX_PHASE_STEP,
  PHASE_REFRESH_MS,
  dayPhase,
  easePhase,
  tintPalette,
} from './time-of-day';
import {
  createSpriteCharacterRenderer,
  type SpriteCharacterRenderer,
  type SpritePartObject,
} from './sprite-character-renderer';
import {
  modeArtGapMessage,
  modeArtGaps,
  strandedPoses,
  type ModeArtGap,
} from './locomotion-pose';
import { gestureHoldMs } from './engagement-pose';
import { GROUND_FILL_SHADE } from './horizon-profile';
import { cameraView, followCamera, intersectsView, type WorldRect } from './level-camera';
import { rideArtProblems, rideBobPx, rideFor, rideFrameKey, ridePlacement, type RideArtSize } from './ride';
import { depthPlan, interleavedDepths, type DepthGroup, type DepthPlan } from './depth-plan';
import { stopSubjectsFor } from './stand-off';
import { backingBounds, backingTuning, createBacking, facingForward, type BackingWatch } from './backing';
import { createKeyPresses, directionPressed, pressedAny, type KeyPresses } from './key-presses';
import { canvasSkyProfile, medianRowColour, type Rgba, type SkyStop, type SkyTopLayer } from './sky-top';
import type { SceneLevel } from './level-document';
import { MAX_STEP_SECONDS, applyBounds, createLocomotion } from './locomotion';
import { watchExit, type ExitWatch } from './level-exit';
import {
  createTouchControls,
  hitTest,
  minTouchTargetPx,
  type TargetRect,
  type TouchControls,
  type ViewPoint,
} from './touch-controls';
import { applyEffect } from './visual-effects';
import type { PlayableMarker } from './playable-marker';
import type { SceneProbe } from './scene-probe';
import type { RenderProfile } from './visual-tier';

/**
 * LevelScene — one Phaser scene, every level.
 *
 * There is no Ottawa in this file. Everything it draws, every number it moves
 * by and every place it puts something comes out of `content/levels/<id>.json`
 * via `SceneLevel`: the parallax bands, the ground polyline, the camera framing,
 * the points of interest, the character placements and the locomotion tuning.
 * That is task 1.13's acceptance stated as a property of the source — the test
 * of this work is not "Ottawa renders" but "a second level would need no code",
 * and `tests/unit/adapters/phaser/level-is-data-only.test.ts` is the gate that
 * keeps it true by refusing any level id, any Ottawa vocabulary and any mode
 * name in this directory's non-comment source.
 *
 * ## What is placeholder, and where the seam is
 *
 * Art has not produced backgrounds yet (task 1.9), so every `key` and `artKey`
 * this level names has no texture behind it. Rather than draw nothing, the scene
 * checks the texture manager and falls back to a flat shape in the level's own
 * theme colours. When an atlas lands under those keys the branch takes the other
 * side and *nothing else changes*: the layer's depth, scroll factor, offset and
 * repeat all come from the document either way. The placeholder is deliberately
 * plain — bands and silhouettes — so nobody mistakes it for art and so it costs
 * almost nothing to throw away.
 *
 * ## Cost
 *
 * The background gradient is one screen of fill, the placeholder bands are
 * shallow (`PLACEHOLDER_BAND_HEIGHT`) rather than reaching the bottom of the
 * world, and the ground is one polygon. Everything static is drawn once into a
 * Graphics and never touched again; the only per-frame drawing is the snow,
 * which is skipped below the medium tier and under reduced motion. That keeps
 * the whole level near 2x screen area against CLAUDE.md's 4x overdraw budget,
 * with the other half left for characters and the HUD.
 *
 * ## Input, and what this is not
 *
 * The keyboard and pointer reading below is a stand-in for `InputPort`
 * (`app/application/ports/input.ts`), which is still PROVISIONAL and belongs to
 * task 1.15 along with the on-screen hold-to-move controls. It produces a
 * `LocomotionIntent` and nothing else, which is the same shape the real adapter
 * will produce, so replacing it is a constructor argument rather than a rewrite.
 * The HUD, the dialogue, the POI card and the interact prompt are accessible DOM
 * and are not this file's: the canvas is `aria-hidden` and always will be.
 */

/** Horizontal bands used to fake the sky gradient, as in `boot-scene.ts`. */
const GRADIENT_BANDS = 64;

/**
 * Keep a summed intent inside -1 … 1.
 *
 * The keyboard and the finger are added rather than switched between, so a
 * player holding a key and the glass at the same time asks for 2 and gets 1.
 * Adding is deliberate: it is what makes holding left on the keyboard and right
 * with a thumb cancel to a stop, which is the answer a player expects from two
 * opposing inputs and is the same one two opposing keys already give.
 */
const clampAxis = (value: number): number => Math.max(-1, Math.min(1, value));

/*
 * Depths are not constants here any more. Every non-player character drew at
 * `500 + z` and the player at `501 + z`, so the two puppets shuffled together
 * wherever the player stood on one — see `depth-plan.ts`, which now owns every
 * band, and `#depths`, which is that plan for this level.
 */

/**
 * The actor placeholder, in design pixels.
 *
 * Still here, and still the right thing: it is what is drawn when the rig or the
 * atlas is not available, and a level a player can walk beats a blank screen.
 * What changed in task 1.12 is that it is no longer *all* that is ever drawn —
 * see `#paintCharacters`, and `data-actors-drawn` on the scene probe, which is
 * what stops a placeholder shipping unnoticed a second time.
 */
const ACTOR_WIDTH = 68;
const ACTOR_HEIGHT = 150;

/**
 * What the player is called in a diagnostic.
 *
 * Not a character id and deliberately not one: which artboard the player wears
 * is read from the rig (`character-cast.ts`), and a level's or a rig's
 * vocabulary may not appear in this directory at all
 * (`level-is-data-only.test.ts`). This is a word for a log line.
 */
const PLAYER_SUBJECT = 'the player';

/**
 * Below this, a character is standing rather than moving, design px/s.
 *
 * The rig's `moving` input is a boolean and the physics is continuous, so
 * something has to draw the line. One pixel a second is a character being
 * nudged by a rounding error, not one taking a step.
 */
const MOVING_THRESHOLD_PX_S = 1;

/**
 * The rig's own name for "somebody was engaged", fired on the character engaged.
 *
 * The rig's vocabulary, not a level's: `sprite-character-renderer.ts` already
 * transcribes the selector rule that reads it. A rig that declares no such
 * trigger answers `character.input.unknown` and the `Result` is dropped, which
 * is correct — an engagement is still an engagement without an animation for it.
 */
const INTERACT_TRIGGER = 'interact';
/** The rig state that trigger selects. The same word, and a different thing. */
const INTERACT_STATE = 'interact';

/** How deep the surface sheen runs below the ground line, design pixels. */
const SHEEN_DEPTH = 34;

/** Where the horizon haze sits and how tall it is, as a fraction and design pixels. */
const HAZE_TOP_FRACTION = 0.44;
const HAZE_HEIGHT = 180;

/** Flake radius range, design pixels. */
const FLAKE_MIN_RADIUS = 3;
const FLAKE_MAX_RADIUS = 8;

/**
 * Every key the level reads, as Phaser key names.
 *
 * One list, because it is also the list that must exist *before* the level says
 * it is playable — see `#bindInput`. Two bindings per action, arrows and the
 * WASD block, so the level is playable with either hand; `#sampleIntent` is
 * where they are summed into an intent.
 */
const LEFT_KEYS = ['LEFT', 'A'] as const;
const RIGHT_KEYS = ['RIGHT', 'D'] as const;
const JUMP_KEYS = ['SPACE', 'UP', 'W'] as const;
const INTERACT_KEYS = ['E', 'ENTER'] as const;
const READ_KEYS = [...LEFT_KEYS, ...RIGHT_KEYS, ...JUMP_KEYS, ...INTERACT_KEYS] as const;

/**
 * A texture source a 2D canvas can draw: an image, a canvas or a bitmap.
 *
 * Phaser's `getSourceImage` may also answer with a render texture or a GL
 * wrapper, which `drawImage` cannot take; those are skipped rather than thrown on
 * (`#textureRowColour`). Each constructor is checked for existence first, so a
 * runtime without one of them is a `false` and not a `ReferenceError`.
 */
function isDrawableSource(
  source: unknown,
): source is HTMLImageElement | HTMLCanvasElement | ImageBitmap {
  return (
    (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) ||
    (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) ||
    (typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap)
  );
}

export interface LevelSceneOptions {
  readonly level: SceneLevel;
  readonly designWidth: number;
  readonly designHeight: number;
  /** The `?e2e=1` probe, or `null` on a normal load. Output only. */
  readonly probe: SceneProbe | null;
  /** Present exactly while the level accepts input. */
  readonly marker: PlayableMarker | null;
  /** The tier the renderer probe measured. `null` until the first window closes. */
  readonly profile: RenderProfile | null;
  /**
   * The textures this level draws from, resolved from `assets/dist/manifest.json`
   * by `level-assets.ts` before the scene was constructed.
   *
   * Empty is legal and is what a build with no art produces: every layer takes
   * the placeholder band. It is **not** silent any more — `create` publishes
   * `layers` and `layersTextured` and the e2e suite fails when they differ,
   * which is the assertion that was missing while the deployed level drew no
   * art at all.
   */
  readonly assets?: readonly LoadRequest[];
  /**
   * The shared character rig, `content/characters/rig.json`, or `null`.
   *
   * The document, already parsed and validated — the adapter never opens it
   * (ADR-0022: an adapter doing content I/O bypasses ajv, the cache and
   * `unload`). `null` means no rig arrived and every character falls back to the
   * placeholder, visibly and countably rather than silently.
   */
  readonly rig?: RigDocument | null;
  /**
   * The appearance the player chose in the character creator, slot -> option.
   *
   * Absent — which is what an unwired build passes — dresses the player in the
   * artboard's own `skins`, then each slot's `fallback`. That is a complete
   * character rather than a naked one, so the creator can be joined to this
   * later without the level being broken until it is. An option the rig does not
   * offer is refused by the renderer with `character.skin.unknown` rather than
   * silently ignored.
   */
  readonly playerSkins?: Readonly<Record<string, string>>;
  /**
   * The device clock, injectable.
   *
   * The level's sky follows the real time of day (`time-of-day.ts`). It is a
   * function rather than a `Date` because a level outlives an instant: the scene
   * re-reads it every few seconds so that a session running past sunset, over
   * midnight, or through a timezone change ends up where the clock is.
   */
  readonly now?: () => Date;
  /** Called once the first playable frame exists. */
  readonly onReady?: (levelId: string) => void;
  /**
   * Every fact the level publishes, as it happens.
   *
   * The composition root turns these into typed bus events, which is how the
   * HUD, the POI card and the live region hear about them without anything in
   * this directory importing `app/ui` (ADR-0005). The probe's trace is *also*
   * fed, unchanged: the trace answers "was it emitted, on which frame", which a
   * bus subscription cannot, and the bus reaches the parts of the game a
   * Playwright assertion is not.
   */
  readonly onEvent?: SceneEventListener;
  /**
   * The moments a level finishes something — see `SCENE_MILESTONE_NAMES`.
   *
   * A second channel rather than two more `SceneEventName`s, because those are
   * type-checked against `app/ui`'s own union in the composition root and the UI
   * has no copy for a finished quest yet. Publishing them here means the engine's
   * half is done and observable — the probe's trace carries them, so their
   * ordering relative to `poi/engaged` is assertable — and the UI's half is one
   * subscription when the words exist.
   */
  readonly onMilestone?: SceneMilestoneListener;
  /**
   * What the canvas draws down its sky, as gradient stops, whenever it changes.
   *
   * The page paints the letterbox with it: the band above a letterboxed canvas
   * takes the first stop's colour (ADR-0044), and the desktop side panels take
   * the whole list, so the level's sky continues past the canvas edge instead of
   * a straight ramp running through it (CLAUDE.md, ADR-0002).
   *
   * One report rather than two, and the top colour is the first stop rather than
   * a second number, so the band and the panels cannot disagree about the row
   * they share. Reported at `create`, and again only when the answer moves: the
   * tier switched a layer off, the time-of-day tint repainted a sky that shows,
   * or the camera scrolled different rows onto the screen. Never on a frame
   * where nothing moved.
   */
  readonly onSkyBand?: (stops: readonly SkyStop[]) => void;
}

/** One placeholder or textured parallax band, plus the document row it came from. */
interface LayerView {
  readonly key: string;
  readonly object: Phaser.GameObjects.GameObject & {
    setScrollFactor(x: number, y: number): unknown;
    setVisible(visible: boolean): unknown;
    setDepth(depth: number): unknown;
    /* Read back rather than remembered: the tier switches layers off through
       `setVisible`, and a second copy of that state is a second thing to keep
       in step. */
    readonly visible: boolean;
  };
  /**
   * A repeating band is drawn **viewport-wide and scrolled by its tile offset**,
   * not world-wide and scrolled by the camera.
   *
   * The difference is the whole cost of a parallax layer. Ottawa is 9000 design
   * pixels long and the camera sees 1080 of them, so a `tileSprite` sized to the
   * world submits a quad eight times wider than anything that can be seen, and —
   * because these textures are not power-of-two — Phaser builds a POT copy of it
   * to make `repeat` wrap. Six of those is megabytes of texture nobody asked for
   * and eight screens of geometry per frame. Pinning the quad to the viewport
   * and advancing `tilePositionX` instead draws exactly one screen per layer and
   * needs no copy.
   *
   * `factorX` is the layer's parallax rate, and it lives here rather than on the
   * game object because the object's `scrollFactor.x` is now always 0 — the quad
   * does not move, its contents do. `null` for a layer that is not tiled.
   */
  readonly tiled: { factorX: number } | null;
}

interface Flake {
  x: number;
  y: number;
  radius: number;
  speed: number;
  drift: number;
}

export class LevelScene extends Phaser.Scene {
  static readonly KEY = 'level';

  readonly #options: LevelSceneOptions;
  readonly #effects: LevelEffects;
  /**
   * Not `readonly`: `setAutoMove` rebuilds it.
   *
   * The accessibility option is `drive: 'auto'` on the tuning and nothing else,
   * so turning it on is one new strategy over the same level data rather than a
   * branch in `#sampleIntent`. The tuning the document authored stays in
   * `#tuning`, which is what reach, jump and the animation binding still read.
   */
  #locomotion: ReturnType<typeof createLocomotion>;
  /**
   * The same mode with its brake on, built once.
   *
   * Stepped instead of {@link #locomotion} on the frames a drive — held or
   * automatic — is being brought to rest at a subject, so a halt is the mode's
   * own `turnAcceleration` and no direction is ever synthesised into
   * `LocomotionIntent.move`. See `auto-stop.ts`; it does not depend on the
   * accessibility option, so it is built with the tuning and never rebuilt.
   */
  readonly #braking: ReturnType<typeof createLocomotion>;
  /**
   * What a drive stops for, and what lets it go again (ADR-0032).
   *
   * The rule is `auto-stop.ts`'s, not this file's, for the reason `level-exit.ts`
   * gives: `environment: 'node'` cannot load a module that imports Phaser, so a
   * rule left in here is a rule proved only by a browser.
   */
  readonly #autoStop: AutoStopWatch;
  /**
   * A ride with a front, backing up (ADR-0043, `backing.ts`): never on its own,
   * held, capped at the ride's own number, and waiting at rest afterwards until
   * a press forward or an engagement. Inert for a ride that turns, or none.
   */
  readonly #backingWatch: BackingWatch;
  /** The same mode backing up. Stepped only on the frames `#backingWatch` says so. */
  readonly #backing: ReturnType<typeof createLocomotion>;
  /**
   * The keys that went down since the last frame (ADR-0043, `key-presses.ts`).
   *
   * `Key.isDown` is read once a frame, so a key let go and pressed again — or
   * pressed and let go — between two frames never happened as far as it can
   * tell. This is what makes a quick tap of the interact key engage, and a quick
   * let-go-and-press-again leave a stop, exactly as a finger does.
   */
  readonly #keyPresses: KeyPresses = createKeyPresses();
  /** The direction a key press began toward this frame, for the auto-stop. */
  #pressBegan: -1 | 0 | 1 = 0;
  readonly #tuning: LocomotionTuning;
  /**
   * What carries the player in the mode they spawn in, or `null` (ADR-0031).
   *
   * Resolved once, like `#tuning`, because the mode is fixed for the life of
   * the scene. The art objects are made in `#paintRide` and placed every frame in
   * `#placeRide`, and `#riderY` is where the rig is drawn — the physics position
   * is never moved, so the camera, reach and the auto-stop see exactly what they
   * saw before a ride existed.
   */
  readonly #ride: Ride | null;
  /** Each layer's image, and the texture it shows now, so a frame is set only when it changes (ADR-0035). */
  #rideArt: { readonly object: Phaser.GameObjects.Image; readonly layer: RideArt; shown: string }[] = [];
  #rideTrack: Phaser.GameObjects.TileSprite | null = null;
  /** The strip over the ground fill (ADR-0042), or `null` when its art did not load. */
  #groundDressing: Phaser.GameObjects.TileSprite | null = null;
  #rideSize: RideArtSize | null = null;
  #ridesDrawn = 0;
  #rideDistancePx = 0;
  #riderY = 0;
  readonly #bounds: LevelBounds;
  /**
   * The end of the level, and the latch that lets it be announced once per
   * arrival.
   *
   * Built from the document, so it is fixed for the life of the scene and the
   * per-frame cost is one or two comparisons.
   * The rule it holds is `level-exit.ts`'s, not this file's: a scene that
   * decided where a level ends would be a rule no `environment: 'node'` test
   * could reach.
   */
  readonly #exit: ExitWatch;
  /**
   * The `Key` objects, created at bind time and read every frame.
   *
   * A map rather than nine fields, and populated in one loop over
   * {@link READ_KEYS}, so "the keys the scene reads" and "the keys the scene
   * registered before it said it was playable" cannot be two different lists.
   */
  readonly #keys = new Map<string, Phaser.Input.Keyboard.Key>();
  /** Fingers on the glass, and what they mean. Pure; see `touch-controls.ts`. */
  readonly #touch: TouchControls = createTouchControls();
  #autoMove = false;

  #state: LocomotionState;
  #camera: Vec2 = { x: 0, y: 0 };
  #profile: RenderProfile | null;
  #layers: LayerView[] = [];
  #player: Phaser.GameObjects.Graphics | null = null;
  #sheen: Phaser.GameObjects.Graphics | null = null;
  #haze: Phaser.GameObjects.Graphics | null = null;
  #snowGraphics: Phaser.GameObjects.Graphics | null = null;
  #flakes: Flake[] = [];
  #snowQuantity = 0;
  #inReach = new Set<string>();
  /**
   * Everything engageable, with the rectangle it was actually drawn at.
   *
   * `position` is what reach is measured from and `rect` is what a finger has to
   * land on. They are different questions and were one field: reach is a
   * horizontal distance from the player, a hit area is a box on the glass that
   * `touch-controls.ts` grows to 44 pt before testing it.
   */
  #reachTargets: readonly {
    readonly id: string;
    readonly position: Vec2;
    readonly npc: boolean;
    readonly rect: TargetRect;
    /** Where inside `rect` the art is, for the mark to point at (ADR-0049). */
    readonly art: ArtSilhouette | null;
    /** What the mark keeps off: the player at a stop here and wherever they walk, and every other character. */
    readonly clear: readonly TargetRect[];
  }[] = [];
  /**
   * The same set again, as `auto-stop.ts` wants it: an id and a world x.
   *
   * Derived once with `#reachTargets` rather than mapped every frame, because
   * this is read on every update and the list does not change for the life of
   * the level.
   */
  #stopSubjects: readonly AutoStopSubject[] = [];
  /** Where each engageable thing was drawn, filled in by the paint passes. */
  readonly #drawnRects = new Map<string, TargetRect>();
  /**
   * Where inside each drawn rectangle the art actually is (ADR-0049): a texture's
   * alpha for a landmark, the rig's frame windows for a character. A rectangle's
   * top edge is not the art, and a mark placed over one floated in empty sky.
   */
  readonly #drawnArt = new Map<string, ArtSilhouette>();
  /** The player's standing figure about their feet, for the probe's mark-on-actor count. */
  #playerFigure: FigureBox | null = null;
  /** Where each placed character's art was drawn, for the same count. */
  #actorBoxes: readonly TargetRect[] = [];
  /**
   * The ride's art in each texture it shows at rest, in its own pixels, a band
   * per column: what a stop reads to keep a ride off a character's feet
   * (ADR-0049). Empty for no ride, or a ride whose art could not be read.
   */
  #rideSilhouettes: readonly ArtSilhouette[] = [];
  #jumpQueued = false;
  #interactQueued = false;
  /** An engaged puppet to advance to its gesture on the frame a pause freezes. See `#holdGesture`. */
  #heldGesture: { readonly renderer: ICharacterRenderer; readonly holdMs: number } | null = null;
  /**
   * The level's palette under the current sky, and where the sky currently is.
   *
   * `#palette` is `level.palette` after `tintPalette`, and **every paint pass
   * reads it rather than the document**, so the tint is one substitution instead
   * of a branch in each of them. `#phase` moves toward the device clock in
   * bounded steps (`easePhase`), which is what makes midnight and a timezone
   * change a drift rather than a cut.
   */
  #phase: number;
  #palette: ThemeColours;
  /**
   * Everything whose colour comes from the palette, as the closure that draws it.
   *
   * Registered by each paint pass and re-run only when the tinted palette
   * actually changes an 8-bit channel — which most refreshes do not. The
   * alternative, a `#repaintSky` per surface, is the same code with five places
   * to forget one of them; the placeholder bands and the landmark silhouette
   * were exactly the two somebody would forget, and they are the two that would
   * then sit in daylight colours against an evening sky.
   */
  readonly #repaintables: (() => void)[] = [];
  /** The rig inputs this rig actually declares and this scene can compute. */
  #driven: { readonly bools: readonly string[]; readonly numbers: readonly string[] } = {
    bools: [],
    numbers: [],
  };
  /**
   * The player, composed from the rig. `null` means the placeholder is on screen.
   *
   * Typed as the sprite backend's own view rather than the port, so the scene
   * can publish `mode` and `pose` — what the puppet is *actually* drawing —
   * onto the probe. Everything the scene *does* to the player goes through
   * `ICharacterRenderer`; the two extra fields are read-only and exist to be
   * observed. See `SpriteCharacterRenderer`.
   */
  #playerCharacter: SpriteCharacterRenderer | null = null;
  /** Placed characters by id, so an engagement can reach the one it engaged. */
  readonly #placed = new Map<string, ICharacterRenderer>();
  /** The tappable marks, by subject id, and the state each was last drawn in. */
  readonly #marks = new Map<
    string,
    { readonly object: Phaser.GameObjects.Graphics; mark: AffordanceMark }
  >();
  /** Subjects the domain has reported finished. Fed by {@link markCompleted}. */
  readonly #completed = new Set<string>();
  /** Milliseconds the level has been running, for the ready mark's pulse. */
  #elapsedMs = 0;
  /**
   * Characters that fell back to a placeholder shape, including the player.
   *
   * Counted separately from `#actorsDrawn` because the player is not in
   * `data-actors` — `level.pois.length + level.characters.length` does not
   * include them — so a player-shaped hole was invisible to every existing
   * counter. This one cannot be: it is published as `data-placeholders` and a
   * healthy level reads 0.
   */
  #placeholders = 0;
  /**
   * Modes this level declares that the rig has no art for.
   *
   * The counter the "it says skating and she walks" defect had no equivalent
   * of. A missing character part had `#placeholders`; a missing *mode* had
   * nothing at all, so a rig with no skating in it drew a walking figure under
   * a HUD that said Skating and every gate read full marks. Published as
   * `data-mode-gaps` and 0 on a level whose art has landed.
   */
  #modeGaps: readonly ModeArtGap[] = [];
  /** Set once the level's stamp has been reported. See {@link markLevelComplete}. */
  #levelComplete = false;
  /** The five-second clock that lets the sky follow the device's time of day. */
  #skyClock: Phaser.Time.TimerEvent | null = null;
  /** The stop list last handed to `onSkyBand`, written out, so an unchanged answer is not sent twice. */
  #skyBand: string | null = null;
  /** The camera row that answer was worked out at. `NaN` until the first one. */
  #skyBandCameraY = Number.NaN;
  /** One colour per texture row asked for. A row of a loaded image never changes. */
  readonly #rowColours = new Map<string, Rgba | null>();
  /** The rig's atlas key, resolved once. `undefined` is "not asked yet". */
  #atlasKey: string | null | undefined = undefined;
  /**
   * Where everything draws, front to back, for this level (`depth-plan.ts`).
   *
   * Fixed for the life of the scene: the number of characters and the rig's
   * `z` range are both known at construction, and a slot that moved after a
   * character was dressed would leave its parts in the old one.
   */
  readonly #depths: DepthPlan;
  /**
   * Every composed character's part objects, by who they belong to.
   *
   * Kept only so `data-parts-interleaved` can be read off the display list the
   * frame the level is ready. The renderer owns the parts and destroys them;
   * this holds references, and a destroyed part is skipped by asking the display
   * list, not by trusting this list.
   */
  #partGroups: { readonly owner: string; readonly parts: Phaser.GameObjects.Image[] }[] = [];
  /** The target a tap landed on, waiting for the next `#sampleIntent`. */
  #tapTarget: string | null = null;
  /** That same target, handed to the frame that is running. */
  #pendingEngage: string | null = null;
  #ready = false;

  constructor(options: LevelSceneOptions) {
    super(LevelScene.KEY);
    this.#options = options;
    this.#profile = options.profile;

    /* The first entry is the mode the player spawns in — `level.schema.json`
       says so, and it is the only ordering rule a scene needs to know. */
    const tuning = options.level.locomotion[0];
    if (tuning === undefined) {
      throw new TypeError(
        `level "${options.level.id}" declares no locomotion tuning. The schema requires at ` +
          'least one and the parser enforces it, so reaching here means the document was not parsed.',
      );
    }
    this.#tuning = tuning;
    this.#ride = rideFor(options.level.rides, tuning.mode);
    /* One slot per character the level places and one for the player, each as
       deep as the rig's `z` range, so no two puppets can shuffle together. */
    this.#depths = depthPlan({
      characters: options.level.characters.length,
      partZ: options.rig?.parts.map((part) => part.z) ?? [],
    });
    this.#locomotion = createLocomotion(tuning);
    this.#braking = createLocomotion(brakingTuning(tuning));
    this.#autoStop = createAutoStop(tuning);
    this.#backingWatch = createBacking(this.#ride);
    this.#backing = createLocomotion(backingTuning(tuning, this.#ride));
    /* A ride that backs up stops where its tail meets the world's edge, so the
       camera, which stops scrolling there, keeps the rider on the glass
       (ADR-0043). Every other level's bounds are exactly `levelBounds`. */
    this.#bounds = backingBounds(levelBounds(options.level.ground, options.level.size), this.#ride);
    /*
     * `designWidth / zoom` is what the camera can see, in world units, at every
     * render scale: `#followInput` multiplies both by the backing scale and the
     * quotient is what `followCamera` uses. So the end of the level is in the
     * same place on a phone, on a tablet and on a centred desktop canvas.
     */
    this.#exit = watchExit({
      bounds: this.#bounds,
      spawnX: options.level.spawn.x,
      viewWidth: options.designWidth / options.level.camera.zoom,
    });
    this.#state = this.#locomotion.spawn(
      options.level.spawn.x,
      groundYAt(options.level.ground, options.level.spawn.x),
      'right',
    );
    this.#riderY = this.#state.y;
    this.#effects = createLevelEffects({
      layers: options.level.layers,
      /* The level's own weather, never a constant: a level that declares
         `none` asks for nothing, so it emits 0 at every tier. */
      requestedParticles: requestedParticlesFor(options.level.weather),
    });

    /* Read once here so the first frame is already at the right time of day; a
       level that faded in from noon would be a worse answer than no feature. */
    this.#phase = dayPhase(this.#clock());
    this.#palette = tintPalette(options.level.palette, { phase: this.#phase });
  }

  /** The device clock, or the injected one. Never called on a frame. */
  #clock(): Date {
    return this.#options.now?.() ?? new Date();
  }

  /** What each effect would do on this device. Read by a test and by the debug overlay. */
  effectPlan(): readonly { readonly id: string; readonly path: string }[] {
    const profile = this.#profile;
    return profile === null ? [] : this.#effects.registry.plan(profile);
  }

  /**
   * Ask Phaser for every texture the level resolved.
   *
   * Phaser runs `preload` and holds `create` until the queue drains, so by the
   * time a layer asks `textures.exists(key)` the answer is the truth about this
   * build rather than "nobody has tried yet" — which is what it used to be, for
   * every layer, in production.
   */
  preload(): void {
    for (const request of this.#options.assets ?? []) {
      if (request.kind === 'image') this.load.image(request.key, request.url);
      else this.load.atlas(request.key, request.textureUrl, request.dataUrl);
    }
  }

  create(): void {
    const { level, designWidth } = this.#options;

    /*
     * What the rig lets this scene say, worked out once.
     *
     * And what the level document says that the rig has never heard of: the
     * `locomotion[].animation` binding names rig inputs and **nothing joins the
     * two documents**, so a level can drive a state that does not exist and the
     * only symptom is a character that never enters it. Reported here, once per
     * level, rather than sixty times a second from inside a setter.
     */
    this.#driven = drivableInputs(this.#options.rig);
    const unbound = unboundAnimationInputs(
      this.#options.rig,
      level.locomotion.flatMap((tuning) =>
        [
          tuning.animation.speedInput,
          tuning.animation.jumpTrigger,
          tuning.animation.landTrigger,
        ].filter((name): name is string => name !== undefined),
      ),
    );
    if (unbound.length > 0) {
      console.error(
        `[level] this level's locomotion animation binding names ${unbound.join(', ')}, which ` +
          `content/characters/rig.json does not declare as state-machine inputs. Those states ` +
          `can never be entered. The rig owns the vocabulary (ADR-0017), and ` +
          `tests/unit/contracts/a-level-binds-only-what-the-rig-declares.test.ts holds every ` +
          `shipped level to it, so this is a document CI never saw; the scene drives only what ` +
          `the rig declares. The binding is not what animates a mode either — see ` +
          `locomotion-pose.ts; it names inputs, and every shipped level names the same three.`,
      );
    }

    /*
     * Can the rig draw the way this level moves?
     *
     * Asked here, once, and answered on the console in every build — because the
     * alternative is what shipped: a level declaring a mode the rig has nothing
     * for drew the base cycle, the HUD named the mode anyway, and the two
     * disagreeing was invisible to every counter the scene had. The atlas is
     * asked as well as the rig: equipment declared in a page nobody packed is
     * the same experience as equipment nobody drew.
     */
    this.#modeGaps = modeArtGaps(
      this.#options.rig,
      level.locomotion.map((tuning) => tuning.mode),
      (frame) => {
        const rig = this.#options.rig ?? null;
        const atlas = rig === null ? null : this.#rigAtlas(rig);
        return atlas !== null && this.textures.exists(atlas) && this.textures.get(atlas).has(frame);
      },
    );
    for (const gap of this.#modeGaps) console.error(`[level] ${modeArtGapMessage(gap)}`);

    const stranded = strandedPoses(this.#options.rig);
    if (stranded.length > 0) {
      console.error(
        `[level] the rig declares the mode poses ${stranded.join(', ')}, each of which re-poses ` +
          `a state the rig does not have and the selector therefore never selects. They can ` +
          `never be played. A pose that never appears is the same defect as an input nothing ` +
          `declares, pointed the other way.`,
      );
    }

    this.#paintSky();
    this.#buildLayers();
    this.#paintGround();
    this.#paintOverlays();
    this.#paintGroundDressing();
    this.#paintPois();
    this.#paintCharacters();
    this.#paintPlayer();
    this.#paintRide();
    this.#buildSnow();
    this.#buildTargets();

    const camera = this.cameras.main;
    /*
     * Zoom from the top-left, not from the middle.
     *
     * Phaser anchors zoom on the camera's origin, which defaults to the centre.
     * Every screen-locked thing this scene draws — the sky gradient, the
     * parallax bands, the two overlays, the snow — is positioned in *design*
     * coordinates from (0, 0), so a centre-anchored zoom of 0.75 would render
     * them inset by 12.5% of the canvas on the left and overflowing it on the
     * right. At origin (0, 0) a design rect from (0,0) to (1080,1920) lands
     * exactly on a canvas of 1080z x 1920z, whatever z is.
     *
     * This was latent before and cost nothing only because Ottawa's zoom is 1.
     * `renderScale` is what makes z leave 1 on a real device, so it is fixed
     * here rather than discovered as a band of background colour down one edge.
     */
    fitCameraToDesign(camera, this.scale.gameSize.width, designWidth, level.camera.zoom);
    /*
     * **No `camera.setBounds`.** The scroll is already clamped to the world by
     * `scrollBounds` inside `followCamera`, in design units, and Phaser's own
     * bounds clamp is a second implementation of the same rule in canvas units.
     * Two clamps did not merely duplicate: Phaser's is computed against the
     * camera *origin*, and this camera's origin is (0, 0) so that a render scale
     * can shrink the drawing buffer without moving anything (see
     * `design-viewport.ts`). With bounds set, that clamp pushed `scrollY` to
     * 266.7 on a level whose world is exactly one viewport tall and whose scroll
     * should be 0 — the whole level sat 266 design pixels off, silently, because
     * both numbers looked plausible.
     *
     * So there is one clamp, it is ours, it is unit tested in
     * `level-camera.test.ts`, and it works in the units the level document is
     * written in.
     */
    this.#camera = followCamera(this.#followInput(0));
    camera.setScroll(this.#camera.x, this.#camera.y);
    /* After the camera, because the track strip is fixed to the world through
       the camera's scroll; and the rider is re-seated before the first frame. */
    this.#placeRide(0);
    this.#player?.setPosition(this.#state.x, this.#riderY);
    this.#playerCharacter?.setPosition(this.#state.x, this.#riderY);

    this.#buildAffordances();
    this.#bindInput();
    this.applyProfile(this.#profile);
    /* After the layers, the camera and the tier: every input to the first row.
       `applyProfile` reports too, but returns before it with no tier yet. */
    this.#reportSkyBand();
    this.#startSkyClock();

    this.#ready = true;
    this.#options.marker?.show();
    this.#options.probe?.publish({
      level: level.id,
      mode: this.#tuning.mode,
      /* Read back from the puppet, never restated from `#tuning`. Restating it
         would make the two attributes agree by construction and the one thing
         they exist to catch — the rig not being told — unobservable again. */
      ...this.#characterModeSnapshot(),
      paused: false,
      playerX: this.#state.x,
      playerY: this.#state.y,
      speed: 0,
      facing: this.#state.facing,
      grounded: true,
      cameraX: this.#camera.x,
      layers: level.layers.length,
      layersTextured: this.#texturedLayers,
      rides: this.#ride === null ? 0 : 1,
      ridesDrawn: this.#ridesDrawn,
      actors: level.pois.length + level.characters.length,
      actorsDrawn: this.#actorsDrawn,
      /* What the ADR-0003 filter examined on this level and what it refused.
         `actors` counts what is painted and is deliberately every placement, so
         these are the only numbers that can tell a landmark that teaches from
         one that is standing there. */
      claimsExamined: level.claims.examined,
      claimsDrawable: level.claims.drawable,
      claimsRefused: level.claims.refused.length,
      playerDrawn: this.#playerCharacter !== null,
      groundDressingDrawn: this.#groundDressing !== null,
      placeholders: this.#placeholders,
      /* Read off the display list, after every character and the ride exist. */
      partsInterleaved: this.#partsInterleaved(),
      dayPhase: this.#phase,
      ...this.#affordanceCounts(),
      ...this.#visibleArt(),
    });
    this.#emit('level/ready', level.id);
    this.#options.onReady?.(level.id);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.#options.marker?.hide();
      this.#ready = false;
      /* Fingers do not survive a level change, and neither does the listener
         that would keep answering for the game after this scene is gone. */
      this.#touch.cancelAll();
      /* The keys belong to the scene's keyboard plugin, which goes with the
         scene; the map is dropped so nothing holds a `Key` from a level that no
         longer exists. */
      this.#keys.clear();
      this.#keyPresses.forget();
      this.game.events.off(Phaser.Core.Events.BLUR, this.#releaseEveryPointer);
      this.events.off(Phaser.Scenes.Events.PAUSE, this.#releaseEveryPointer);
      this.events.off(Phaser.Scenes.Events.SLEEP, this.#releaseEveryPointer);
      /* Every character releases its parts before the level goes; the atlas is
         the texture manager's and is dropped by the unload path, not here. */
      for (const character of this.#characters) character.dispose();
      this.#characters = [];
      this.#partGroups = [];
      this.#playerCharacter?.dispose();
      this.#playerCharacter = null;
      /* The images go with the scene's display list; the references are dropped
         so nothing holds a texture from a level that no longer exists. */
      this.#rideArt = [];
      this.#rideTrack = null;
      this.#groundDressing = null;
      this.#placed.clear();
      this.#skyClock?.remove();
      this.#skyClock = null;
      /* The rows belong to textures the unload path is about to drop. */
      this.#rowColours.clear();
    });
  }

  /**
   * Re-apply the visual tier.
   *
   * Called at create and again whenever `render-tier-probe.ts` closes a
   * measurement window and changes its mind. Everything the tier controls is
   * re-derived here rather than remembered, so a promotion and a demotion are
   * the same code path and neither can leave a layer half-configured.
   */
  applyProfile(profile: RenderProfile | null): void {
    this.#profile = profile;
    if (profile === null) return;

    /* The renderer may have resized the drawing buffer for this tier just
       before calling us, so the camera's zoom is re-derived rather than
       remembered — same reason everything else here is. */
    fitCameraToDesign(
      this.cameras.main,
      this.scale.gameSize.width,
      this.#options.designWidth,
      this.#options.level.camera.zoom,
    );

    const kept = new Set(
      selectLayers(this.#options.level.layers, profile.parallaxLayers, this.#layerViewport()).map(
        (layer) => layer.key,
      ),
    );
    for (const layer of this.#layers) {
      const visible = kept.has(layer.key);
      layer.object.setVisible(visible);
      /* Pinned to the world first, then the drift effect may override it. A
         skipped effect therefore means "no independent movement", which is what
         reduced motion asks for — not a missing backdrop. */
      this.#setLayerRate(layer, 1, 1);
      if (!visible) continue;
      applyEffect(
        this.#effects.parallaxDrift,
        /*
         * The effect is handed a *target*, not the game object, so a tiled band
         * can take the same instruction — "scroll at this rate" — and honour it
         * by moving its tile offset instead of its quad. `level-effects.ts` is
         * unchanged and still knows nothing about how a layer is drawn, which is
         * what lets its policy stay unit tested with no Phaser.
         */
        {
          layerKey: layer.key,
          setScrollFactor: (x: number, y: number) => this.#setLayerRate(layer, x, y),
        },
        profile,
      );
    }
    this.#scrollLayers();
    this.#scrollGroundDressing();
    /* A tier that switched the sky layer off shows the gradient on the first row. */
    this.#reportSkyBand();

    this.#snowQuantity = 0;
    const emitter = {
      setQuantity: (quantity: number): void => {
        this.#snowQuantity = quantity;
      },
      start: (): void => undefined,
    };
    applyEffect(this.#effects.snowfall, emitter, profile);
    this.#seedFlakes(this.#snowQuantity);
    this.#snowGraphics?.setVisible(this.#snowQuantity > 0);

    /* The two `still` overlays. A skipped effect never touches the alpha, so the
       object is hidden first and only made visible by the effect having run —
       "the tier decided" rather than "nobody set it". */
    for (const [effect, object] of [
      [this.#effects.surfaceSheen, this.#sheen],
      [this.#effects.horizonHaze, this.#haze],
    ] as const) {
      if (object === null) continue;
      object.setVisible(false);
      const target = {
        setAlpha: (alpha: number): void => {
          object.setAlpha(alpha);
          object.setVisible(true);
        },
      };
      applyEffect(effect, target, profile);
    }

    /*
     * Placed characters are driven once, at create, so a tier change after that
     * would leave their `reducedMotion` input stale — a rig state chosen under a
     * motion setting the player has since turned off. Re-driven here rather than
     * every frame: this runs when the tier changes and at nothing else.
     */
    for (const character of this.#placed.values()) {
      this.#driveCharacter(character, {
        grounded: true,
        moving: false,
        speed: 0,
        verticalSpeed: 0,
      });
    }

    this.#options.probe?.publish({
      /* What is falling, counted from the flakes `#updateSnow` draws — not the
         tier's allowance, which is the renderer's `data-particle-allowance`.
         This scene is the only writer of `particles`; the two shared one
         attribute once, and it read 0 at a tier allowing 150. */
      particles: this.#flakes.length,
      parallaxEasing: profile.parallaxEasing,
      tier: profile.tier,
      motion: profile.motion,
    });
  }

  override update(_time: number, delta: number): void {
    if (!this.#ready) return;

    const dt = Math.min(MAX_STEP_SECONDS, Math.max(0, delta / 1000));
    const intent = this.#sampleIntent();

    /*
     * Is the drive being brought to rest at something this frame? (ADR-0032)
     *
     * Asked after the intent, because what the player is pressing is what
     * releases it, and before the step, because the answer chooses which
     * strategy takes the step. `true` is the brake, stepped with the move taken
     * out of the intent — a held drive is caught while the control is still
     * down. The probe below records the intent as sampled, so a trace of a
     * hands-off run still reads as zero on every frame and a held one as held.
     */
    const halted = this.#autoStop.update({
      automatic: this.#automaticDrive(),
      playerX: this.#state.x,
      velocityX: this.#state.velocityX,
      playerMove: intent.move,
      pressBegan: this.#pressBegan,
      subjects: this.#stopSubjects,
    });
    /*
     * Is a ride with a front backing up, or waiting after it? (ADR-0043)
     *
     * Asked every frame, halted or not, so it sees the ride come to rest from
     * backing even when a stop was what brought it to rest. `parked` steps the
     * brake, so nothing moves while the player decides; `backing` steps the
     * held, capped mode; `forward` is the mode as authored, faced forward at rest
     * so an automatic drive never reads a passenger turned round in the seat as
     * the way on. For a ride that turns, or none, the answer is always `forward`
     * and `facingForward` hands the state back unchanged.
     */
    const drive = this.#backingWatch.update({
      velocityX: this.#state.velocityX,
      playerMove: intent.move,
    });

    const step =
      halted || drive === 'parked'
        ? this.#braking.step(this.#state, brakingIntent(intent), dt)
        : drive === 'backing'
          ? this.#backing.step(this.#state, intent, dt)
          : this.#locomotion.step(facingForward(this.#state, this.#ride), intent, dt);
    this.#state = applyBounds(step.state, this.#bounds, this.#tuning, dt);

    for (const event of step.events) this.#publishLocomotionEvent(event.kind);
    this.#updateReach();
    /*
     * Did they just arrive at the end of the level?
     *
     * After the frame's movement and after the reach, so the trace reads in the
     * order it happened — the walk, then whatever came into reach on the way,
     * then the arrival. The latch is inside `#exit`: this is `false` on every
     * frame after the first of an arrival, which is what stops a player
     * jostling at the boundary from opening a completion card sixty times a
     * second. It re-arms once the player has walked back behind the line by a
     * visible step, so arriving again is reported again (ADR-0074).
     *
     * The scene does not decide what the arrival is worth. It is a position, not
     * an achievement, and a player can reach it having answered nothing; whether
     * that finishes the level is `app/bootstrap`'s to say, which is why this is
     * `level/exitReached` and not `level/completed`.
     */
    if (this.#exit.arrived(this.#state.x)) {
      this.#emitMilestone('level/exitReached', this.#options.level.id);
    }

    this.#camera = followCamera(this.#followInput(dt));
    this.cameras.main.setScroll(this.#camera.x, this.#camera.y);
    /* A number compare on every frame, and nothing more on any shipped level: a
       world one viewport tall clamps the vertical scroll to 0, so it never moves. */
    if (this.#camera.y !== this.#skyBandCameraY) this.#reportSkyBand();

    this.#elapsedMs += delta;
    this.#placeRide(dt);
    this.#player?.setPosition(this.#state.x, this.#riderY);
    this.#updatePlayerCharacter(step.animationSpeed, delta);
    this.#updatePlacedCharacters(delta);
    this.#holdGesture();
    this.#updateAffordances();
    this.#scrollLayers();
    this.#scrollGroundDressing();
    this.#updateSnow(dt);

    const probe = this.#options.probe;
    if (probe !== null) {
      probe.recordFrame({
        dtSeconds: dt,
        x: this.#state.x,
        y: this.#state.y,
        velocityX: this.#state.velocityX,
        speed: Math.abs(this.#state.velocityX),
        grounded: this.#state.grounded,
        facing: this.#state.facing,
        intentMove: intent.move,
        cameraX: this.#camera.x,
        marksOnActors: this.#marksOnActors(),
      });
      probe.publish({
        ...this.#characterModeSnapshot(),
        playerX: this.#state.x,
        playerY: this.#state.y,
        speed: Math.abs(this.#state.velocityX),
        facing: this.#state.facing,
        grounded: this.#state.grounded,
        cameraX: this.#camera.x,
        ...this.#affordanceCounts(),
        ...this.#visibleArt(),
      });
    }
  }

  /* ---------------------------------------------------------------- input --- */

  /**
   * Keyboard and pointer, bound once.
   *
   * The keyboard half is untouched by the touch work and must stay that way:
   * keyboard-only play is a requirement, not a fallback (CLAUDE.md,
   * Accessibility), so every branch below adds to what a key already does and
   * none of them replaces it. The two are summed in `#sampleIntent`, which means
   * a player using both at once gets the sum rather than a mode switch.
   *
   * The pointer half handles *every* pointer, not only touch ones. A mouse click
   * on a desktop is a press that ends quickly and without travel, which is a tap,
   * which is a jump — exactly what the single `POINTER_DOWN` handler that used to
   * live here did. Nothing on the desktop got worse.
   *
   * `POINTER_UP_OUTSIDE` is bound alongside `POINTER_UP` because a finger that
   * slides off the canvas before lifting reports only the outside one, and a
   * release nobody hears is a walk that never stops.
   *
   * Times come from `this.time.now`, the scene's own clock, not from
   * `performance.now()`. It advances once per frame, so a press and a release
   * inside one frame are zero milliseconds apart and a press is measured from
   * the frame that saw it — the same clock the level is simulated in, and the
   * one every assertion about this feature is phrased in. The cost is that a
   * tap's measured duration is rounded up by at most one frame: 17 ms at the
   * frame budget, 33 ms on the 30 fps Android target, against a 160 ms window.
   * A tap has to be half again as long as any real one before that rounding
   * could change the answer.
   */
  #bindInput(): void {
    const keyboard = this.input.keyboard;
    keyboard?.addCapture(['LEFT', 'RIGHT', 'UP', 'DOWN', 'SPACE']);

    /*
     * Every key this level reads, created HERE — before the level says it is
     * playable — and not on the first frame that happens to ask for one.
     *
     * This is the dropped first keypress, and it was ours. `KeyboardManager`
     * pushes the DOM event and emits `MANAGER_PROCESS` **synchronously from the
     * `keydown` handler**; `KeyboardPlugin.update` then looks the code up in
     * `this.keys` and, finding nothing there, drops it — the queue is cleared
     * whether or not anybody was listening. `#sampleIntent` used to call
     * `addKey` lazily, so no `Key` existed until the scene's first `update` —
     * one frame after `create` had already shown the playable marker. Any key
     * pressed in that window went nowhere.
     *
     * That closes the window this file owns. A **wider** one is still open and
     * is not ours: `app/bootstrap` writes `data-tn-level="ready"` when
     * `loadLevel` resolves, which is before Phaser has booted the scene at all,
     * so a key pressed then is dropped before this scene exists to hear it. It
     * is reproducible by delaying the level's textures, and the one line that
     * closes it is `GameRendererOptions.onLevelReady`.
     *
     * Nothing recovered from it either: a held key produces no further `keydown`
     * under automation, and under a real OS the browser's auto-repeat is half a
     * second away. The player pressed right, the game did nothing, and the only
     * cure was to let go and press again — which is exactly what
     * `tests/e2e/study-and-settings.spec.ts` had to do to walk at all.
     *
     * `enableCapture: false` on each key for the reason `#sampleIntent` gives:
     * the page keeps its own key handling, so a DOM screen above the canvas is
     * never starved of a keystroke. The five that must not scroll the window are
     * captured plugin-wide on the line above, as before.
     */
    for (const name of READ_KEYS) {
      const key = keyboard?.addKey(name, false);
      if (key === undefined) continue;
      this.#keys.set(name, key);
      /* Every real key-down, whenever it arrives, and never the browser's
         auto-repeat: `Key` emits `down` only on the way down. `#sampleIntent`
         takes them once a frame (ADR-0043). */
      key.on(Phaser.Input.Keyboard.Events.DOWN, (_key: unknown, event: KeyboardEvent) => {
        this.#keyPresses.down(name, event.repeat);
      });
    }

    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      this.#touch.press(pointer.id, this.#viewPointOf(pointer), this.time.now);
    });
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      /* A desktop mouse emits this continuously with nothing pressed, and the
         answer for every one of those is "no pointer is down, there is nothing
         to drag". Checked here rather than inside the gesture module so the
         common case costs a field read instead of a call and an allocation —
         per-frame pointer work is inside the engine cost
         `tests/perf/frame-time.device.ts` measures. */
      if (this.#touch.active === 0) return;
      this.#touch.drag(pointer.id, this.#viewPointOf(pointer), this.time.now);
    });

    const up = (pointer: Phaser.Input.Pointer): void => {
      /*
       * A pointer the browser took away is not a release.
       *
       * `touchcancel` — a notification shade, an incoming call, a system edge
       * gesture — reaches Phaser as a `POINTER_UP` with `wasCanceled` set, and
       * treating it as an ordinary release would fire a jump for an interaction
       * the player never finished. Dropping it also un-latches the walk, which is
       * the bug this arm exists for: without it the player keeps walking while
       * they read a notification, and there is no finger left to stop them.
       */
      if (pointer.wasCanceled) {
        this.#touch.cancel(pointer.id);
        return;
      }
      const tap = this.#touch.release(pointer.id, this.time.now);
      if (tap === null) return;
      this.#resolveTap(tap);
    };
    this.input.on(Phaser.Input.Events.POINTER_UP, up);
    this.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, up);

    /* A backgrounded tab, a phone call answered, an app switch. The pointers
       that were down are gone and no `touchend` is coming for them. */
    this.game.events.on(Phaser.Core.Events.BLUR, this.#releaseEveryPointer);
    this.events.on(Phaser.Scenes.Events.PAUSE, this.#releaseEveryPointer);
    this.events.on(Phaser.Scenes.Events.SLEEP, this.#releaseEveryPointer);
  }

  /** Bound once so `off` can find it again; see `#bindInput` and SHUTDOWN. */
  readonly #releaseEveryPointer = (): void => {
    this.#touch.cancelAll();
    /* A key pressed into a card that has since closed is not a press into the
       level: `Enter` on a focused prompt reaches both, and the card pauses the
       level before the next frame could take it (ADR-0043). */
    this.#keyPresses.forget();
    this.#pressBegan = 0;
    /* The same moment hides the player's hands from the auto-stop: a thumb
       lifted while a card or the menu was open never reached a frame, so the
       first press seen afterwards has to count as a new one (ADR-0032). */
    this.#autoStop.forgetInput();
  };

  /**
   * What a tap meant: engage the thing under it, or jump.
   *
   * Hit-testing runs first and unconditionally, because "tap an NPC or a POI to
   * engage it" has to beat "tap to jump" when the finger lands on one — a player
   * who taps the officer and watches their skater hop has been told the game
   * ignored them.
   *
   * Only targets the level's own `InteractionAffordance` says are engageable are
   * offered to the hit test, which is why a tap on something across the canal
   * jumps: at that distance it is not an NPC, it is scenery, and tapping scenery
   * is tapping the world.
   *
   * The engagement is *queued*, not performed. It is fed back in as
   * `LocomotionIntent.interactPressed` on the next frame so it goes through the
   * strategy exactly as the interact key does — which is what makes a mode with
   * `interaction: null` (a canoe mid-river) refuse a tap without this file
   * knowing that such modes exist.
   */
  #resolveTap(tap: ViewPoint): void {
    const target = hitTest(this.#engageableTargets(), this.#viewToWorld(tap), this.#minTouchWorldPx());
    if (target === null) {
      this.#jumpQueued = true;
      return;
    }
    this.#tapTarget = target.id;
  }

  /** Targets within the mode's reach right now, with the hit area they were drawn at. */
  #engageableTargets(): readonly { readonly id: string; readonly npc: boolean; readonly rect: TargetRect }[] {
    return this.#reachTargets.filter((target) => this.#onOffer(target.id, target.position.x));
  }

  #sampleIntent(): LocomotionIntent {
    /* Read from the keys `#bindInput` created before the level was announced
       playable. Asking `addKey` here instead is what dropped the first press of
       the session; see the note there. A key missing from the map means this
       scene has no keyboard at all — headless, or input disabled — which reads
       as "not held", the same answer it gave before. */
    const down = (key: string): boolean => this.#keys.get(key)?.isDown ?? false;

    /*
     * Two bindings per action, arrows and the WASD block, so the level is
     * playable with either hand. These are Phaser key names and therefore
     * `keyCode`-based; `InputPort` binds by `KeyboardEvent.code` — physical
     * position — and replacing this reader with it is what would make
     * TN-LEVEL-06's AZERTY scenario true rather than nearly true.
     *
     * The touch work did **not** do that, deliberately. `InputPort` describes a
     * device: a set of held/pressed `GameAction`s and a `moveAxis`. Neither half
     * of the design here can be decided from that. "Which side of the player is
     * the finger" needs the player's position on screen, and "what did this tap
     * mean" needs a hit test against the world — both are gameplay facts the
     * port has no way to carry, and pushing either into an input adapter would
     * put the camera and the level's targets behind a port that exists to know
     * about neither. So the port is still `PROVISIONAL` and unconsumed, and what
     * crosses the boundary from `touch-controls.ts` is intent, not events.
     */
    const left = LEFT_KEYS.some(down);
    const right = RIGHT_KEYS.some(down);
    const jumpHeld = JUMP_KEYS.some(down);
    const interact = INTERACT_KEYS.some(down);

    /*
     * What went down since the last frame, which the levels above cannot show
     * (ADR-0043). A tap of the interact key shorter than a frame is a press; a
     * direction let go and pressed again inside one frame is a new press, which
     * is what a held stop waits for. The levels still decide what is held.
     */
    const presses = this.#keyPresses.take();
    this.#pressBegan = directionPressed(presses, LEFT_KEYS, RIGHT_KEYS);

    const jumpPressed =
      this.#jumpQueued || pressedAny(presses, JUMP_KEYS) || (jumpHeld && !this.#lastJumpHeld);
    this.#lastJumpHeld = jumpHeld;
    this.#jumpQueued = false;

    /*
     * The tap's target, handed to this frame and to no other.
     *
     * Assigned every frame rather than cleared conditionally, so a tap the
     * strategy declines — out of reach by the time the frame ran, or a mode that
     * cannot engage at all — cannot sit in the field and fire later.
     */
    this.#pendingEngage = this.#tapTarget;
    this.#tapTarget = null;

    const interactPressed =
      this.#interactQueued ||
      this.#pendingEngage !== null ||
      pressedAny(presses, INTERACT_KEYS) ||
      (interact && !this.#lastInteract);
    this.#lastInteract = interact;
    this.#interactQueued = false;

    /*
     * Keyboard and finger, summed.
     *
     * `#touch.axis` is asked once a frame and takes the player's position on
     * screen, because the direction is "which side of the player is the finger",
     * never "which half of the screen was touched" — the camera carries a lead
     * offset and clamps at the world's edges, so the player's screen position
     * moves and a screen-half rule would invert under a stationary thumb.
     */
    const touch = this.#touch.axis(this.#playerViewX(), this.time.now);
    const move = clampAxis((left ? -1 : 0) + (right ? 1 : 0) + touch);

    return {
      move,
      jumpPressed,
      jumpHeld,
      interactPressed,
      slope: slopeAt(this.#options.level.ground, this.#state.x),
      groundY: groundYAt(this.#options.level.ground, this.#state.x),
    };
  }

  /* ------------------------------------------------ pointer <-> the world --- */

  /**
   * A Phaser pointer in design-space view pixels.
   *
   * `pointer.x` is in *canvas* pixels, and the canvas is `designWidth * scale`
   * wide because the visual tier shrinks the drawing buffer (`design-viewport.ts`).
   * Every threshold in `touch-controls.ts` is in design pixels, so the division
   * has to happen here or a demoted tier would silently halve the dead zone.
   */
  #viewPointOf(pointer: { readonly x: number; readonly y: number }): ViewPoint {
    const scale = this.#backingScale();
    return { x: pointer.x / scale, y: pointer.y / scale };
  }

  /** Where the player is on the glass, in design pixels from the canvas's left edge. */
  #playerViewX(): number {
    return (this.#state.x - this.#camera.x) * this.#options.level.camera.zoom;
  }

  #viewToWorld(point: ViewPoint): ViewPoint {
    const zoom = this.#options.level.camera.zoom;
    return { x: this.#camera.x + point.x / zoom, y: this.#camera.y + point.y / zoom };
  }

  /**
   * CLAUDE.md's 44 pt minimum, in world units.
   *
   * Measured from the canvas the player is touching rather than assumed: the
   * same 44 pt is about 122 design pixels on a 390 px phone and about 95 on a
   * centred desktop portrait canvas. An NPC drawn smaller than this is still
   * drawn smaller than this — what grows is the glass that counts as them.
   */
  #minTouchWorldPx(): number {
    const css = this.scale.displaySize.width;
    return minTouchTargetPx(this.#options.designWidth, css) / this.#options.level.camera.zoom;
  }

  /**
   * Auto-move, the accessibility option (CLAUDE.md, Traversal).
   *
   * Implemented by handing the strategy `drive: 'auto'` rather than by
   * synthesising a direction in this file. `locomotion.ts` already turns that
   * into "keep going the way you are facing" for every mode, so the option costs
   * one field on a tuning object and no scene code at all — and a player with
   * auto-move on still steers and brakes with a held finger or a held key,
   * because a non-zero intent always wins over the drive.
   *
   * NOT WIRED TO THE SETTINGS SCREEN YET. `app/ui/settings-screen.ts` offers the
   * toggle and `app/bootstrap` owns the wire between it and this method; both
   * were being edited by another agent when this landed, so the requirement is
   * reported rather than half-applied.
   */
  setAutoMove(enabled: boolean): void {
    if (this.#autoMove === enabled) return;
    this.#autoMove = enabled;
    this.#locomotion = createLocomotion(
      enabled ? { ...this.#tuning, drive: 'auto' } : this.#tuning,
    );
  }

  get autoMove(): boolean {
    return this.#autoMove;
  }

  /**
   * Is the player being carried rather than holding?
   *
   * True for the accessibility option **and** for a level that declares
   * `drive: "auto"` — prairie-rail's train does, with `requiresStop: true`. One
   * question, so the train's halt and auto-move's halt are the same code path
   * and a level can have the behaviour from JSON alone. A held drive stops at
   * the same subjects on the same line; this only says whether a frame with
   * nothing pressed is still a drive (`auto-stop.ts`).
   */
  #automaticDrive(): boolean {
    return this.#autoMove || this.#tuning.drive === 'auto';
  }

  #lastJumpHeld = false;
  #lastInteract = false;
  /** How many parallax layers drew from a texture. See `SceneSnapshot.layers`. */
  #texturedLayers = 0;
  /** Characters and points of interest that drew real art, not a placeholder. */
  #actorsDrawn = 0;
  #characters: ICharacterRenderer[] = [];
  /**
   * Where each thing that drew real art actually is, in world space.
   *
   * Kept so the probe can answer "is it on the screen" rather than only "was it
   * given a texture" — the distinction a `2/2` on an empty screen did not make.
   * A screen-locked parallax band has no world x, so it is recorded as spanning
   * the level: it is horizontally in view by construction.
   */
  #artBounds: { readonly kind: 'layer' | 'actor'; readonly rect: WorldRect }[] = [];

  /**
   * Canvas pixels per design pixel, read from the scale manager rather than
   * passed in.
   *
   * `GameRenderer` resizes the drawing buffer when the tier changes, and the
   * scale manager is then the one true statement of how big it is. A copy
   * handed to the scene could disagree with the canvas for a frame, and a
   * camera zoom that disagrees with the canvas is a mis-framed level.
   */
  #backingScale(): number {
    return backingScaleOf(this.scale.gameSize.width, this.#options.designWidth);
  }

  /* --------------------------------------------------------------- camera --- */

  #followInput(dtSeconds: number): Parameters<typeof followCamera>[0] {
    const { level, designWidth, designHeight } = this.#options;
    /*
     * Both the viewport and the zoom carry the backing scale, so their quotient
     * — the world the camera can see, which is the only thing `followCamera`
     * uses them for — is unchanged. Framing is therefore identical at every
     * render scale, which is the property `tests/e2e/level-ottawa.spec.ts`'s
     * "between 25 and 70 percent of the canvas width" scenario asserts.
     */
    const scale = this.#backingScale();
    return {
      camera: this.#camera,
      target: { x: this.#state.x, y: this.#state.y },
      velocityX: this.#state.velocityX,
      facing: this.#state.facing,
      tuning: { ...level.camera, zoom: level.camera.zoom * scale },
      viewport: { width: designWidth * scale, height: designHeight * scale },
      world: level.size,
      dtSeconds,
      reducedMotion: this.#profile?.motion === 'reduced',
    };
  }

  /**
   * What the layers have to cover, and where the ground cuts them off.
   *
   * `horizonY` is the highest point of the ground polyline: everything below it
   * is painted over by the ground polygon, so a band down there covers nothing
   * however tall it is. That is what stops the tier keeping Ottawa's canal wall
   * over its sky.
   */
  #layerViewport(): LayerViewport {
    const { level, designWidth, designHeight } = this.#options;
    const horizonY = level.ground.reduce(
      (highest, point) => Math.min(highest, point.y),
      designHeight,
    );
    return {
      width: designWidth,
      height: designHeight,
      horizonY,
      sizeOf: (key) => {
        if (!this.textures.exists(key)) return null;
        const source = this.textures.get(key).getSourceImage();
        return { width: source.width, height: source.height };
      },
    };
  }

  /**
   * How much of the level's art the camera can actually see, right now.
   *
   * `layersTextured` and `actorsDrawn` count what was *given a texture*, and
   * both read full marks on a build whose screen held a gradient, an ice band,
   * snow and one rounded rectangle — the officer and the landmark were drawn
   * faithfully, 1 760 and 4 760 design pixels off to the right. A count whose
   * passing value does not require the outcome is not a check.
   *
   * These two are geometry: a subject whose world bounds do not meet the
   * camera's view is not on the screen. They move as the camera moves, so a test
   * can watch the same counter read `0` with the officer off screen and `1` with
   * the officer in front of the player — which is the built-in falsification the
   * counters they sit beside never had.
   *
   * Honest about its own limit: this proves *occupies screen area*, not *lit a
   * pixel*. A transparent texture would still count. Pixel statistics were tried
   * for the stronger claim and are recorded as not working on this scene in
   * `tests/e2e/level-art.spec.ts`.
   */
  #visibleArt(): { layersVisible: number; actorsVisible: number } {
    const { level, designWidth, designHeight } = this.#options;
    const scale = this.#backingScale();
    const view = cameraView(
      this.#camera,
      { width: designWidth * scale, height: designHeight * scale },
      { ...level.camera, zoom: level.camera.zoom * scale },
    );

    let layersVisible = 0;
    let actorsVisible = 0;
    for (const art of this.#artBounds) {
      if (!intersectsView(art.rect, view)) continue;
      if (art.kind === 'layer') layersVisible += 1;
      else actorsVisible += 1;
    }
    /* A layer the tier switched off is drawn nowhere, whatever its bounds say. */
    const hidden = this.#layers.filter((layer) => !layer.object.visible).length;
    return { layersVisible: Math.max(0, layersVisible - hidden), actorsVisible };
  }

  /* --------------------------------------------------------------- events --- */

  #emit(name: SceneEventName, detail?: string): void {
    if (detail === undefined) this.#options.probe?.recordEvent(name);
    else this.#options.probe?.recordEvent(name, detail);
    /* Spread rather than a conditional call for the same reason the probe above
       has two branches: `exactOptionalPropertyTypes` makes an explicit
       `undefined` a different thing from an omitted argument, and a listener
       that receives `detail: undefined` cannot tell "no subject" from "a subject
       nobody set". */
    if (detail === undefined) this.#options.onEvent?.(name);
    else this.#options.onEvent?.(name, detail);
  }

  /**
   * Locomotion facts, under the names `docs/stories/README.md` fixes.
   *
   * A landing has no name in that list and so is not invented here: the frame
   * trace already carries `grounded` every frame, which is how TN-LEVEL-03's
   * "on landing the horizontal speed is at least 90 percent of the take-off
   * speed" is answered.
   */
  #publishLocomotionEvent(kind: string): void {
    if (kind === 'started-moving') this.#emit('player/moved');
    else if (kind === 'stopped') this.#emit('player/stopped');
    else if (kind === 'took-off') this.#emit('player/jumped');
    else if (kind === 'braked') this.#emit('player/braked');
    else if (kind === 'interaction-requested') this.#engageNearest();
  }

  /**
   * Everything engageable, as one list, so reach is one rule and not two.
   *
   * Built once at create rather than per frame. It was per frame, and it
   * allocated three arrays every time `update` ran — a level's POIs and
   * characters do not move, and sixty allocations a second of a list that never
   * changes is exactly the kind of cost that only shows up on the device that
   * cannot afford it.
   */
  #buildTargets(): void {
    const { level } = this.#options;
    /*
     * The hit area is the rectangle the paint pass actually drew, not a box
     * guessed from the position. A landmark that fell back to its silhouette is
     * 180 x 620; the same landmark with its art is whatever the texture is; a
     * character composed from the rig is `characterSpace`. Reading the drawn
     * rectangle means a tap lands on what the player can see rather than on
     * where the level document says the thing is.
     */
    const fallback = (position: Vec2): TargetRect => ({
      x: position.x - ACTOR_WIDTH / 2,
      y: position.y - ACTOR_HEIGHT,
      width: ACTOR_WIDTH,
      height: ACTOR_HEIGHT,
    });
    const rectFor = (id: string, position: Vec2): TargetRect =>
      this.#drawnRects.get(id) ?? fallback(position);

    /*
     * `reachablePois`, not `pois` and not `teachingPois` — ADR-0003 at the one
     * seam where it shows, and ADR-0029 at the one where it would break.
     *
     * A landmark whose blurb a verifier declined is still painted by
     * `#paintPois`, because the level's picture is composed around it and a hole
     * is a worse lie than a quiet building. Whether it is a **target** depends
     * on whether it has a job besides the blurb:
     *
     *  - **No quest role** — no reach event, so the HUD never offers it; no stop
     *    subject, so an automatic drive does not brake for something that would
     *    not open; no affordance mark; and a tap on it is a tap on scenery (see
     *    `#resolveTap`). The blurb was the whole of what the invitation promised.
     *  - **A `questId`** — still a target. It gives or advances a quest, and that
     *    is still there to do: refusing the claim withholds the card, not the
     *    lighthouse the level's quest is given by. Before this, one refused blurb
     *    at Peggy's Cove made the level's giver unreachable and its quest gone.
     *
     * `teachingPois` answered both questions at once, which is how the second
     * went wrong. This scene still does not know what a quest is; `questId` is
     * the level document declaring the role, and the list is derived in
     * `level-document.ts`.
     */
    /* The subjects a drive stops for, in the order the targets are listed below,
       with a rest point beside every character the rig can measure: a drive
       comes to rest next to a person, not inside them (ADR-0037, `stand-off.ts`). */
    this.#stopSubjects = stopSubjectsFor({
      level,
      rig: this.#options.rig,
      tuning: this.#tuning,
      ride: this.#ride,
      rideArt: this.#rideSilhouettes,
    });
    /* What no mark may be drawn on (ADR-0049, `mark-clearance.ts`): the player's
       head while each of those stops holds them, the player wherever they can
       walk, crown to soles, and every other character standing in the level. */
    const artRect = (id: string, position: Vec2): TargetRect => {
      const drawn = this.#drawnArt.get(id);
      const bounds = drawn === undefined ? null : silhouetteBounds(drawn);
      return bounds === null
        ? rectFor(id, position)
        : { x: bounds.left, y: bounds.top, width: bounds.right - bounds.left, height: bounds.bottom - bounds.top };
    };
    const actors = level.characters.map((character) => ({
      id: character.characterId as string,
      rect: artRect(character.characterId as string, character.position),
    }));
    this.#actorBoxes = actors.map((actor) => actor.rect);
    this.#playerFigure = playerStandingFigure(this.#options.rig, this.#tuning.mode);
    const clearance = markClearance(
      [...level.reachablePois.map((poi) => poi.id as string), ...level.characters.map((c) => c.characterId as string)],
      {
        resting: restingHeadBoxes({
          ground: level.ground,
          subjects: this.#stopSubjects,
          rig: this.#options.rig,
          tuning: this.#tuning,
          ride: this.#ride,
        }),
        walking: walkingFigureBoxes({
          ground: level.ground,
          rig: this.#options.rig,
          tuning: this.#tuning,
          ride: this.#ride,
        }),
        actors,
      },
    );
    const target = (id: string, position: Vec2, npc: boolean) => ({
      id,
      position,
      npc,
      rect: rectFor(id, position),
      art: this.#drawnArt.get(id) ?? null,
      clear: clearance.get(id) ?? [],
    });
    this.#reachTargets = [
      ...level.reachablePois.map((poi) => target(poi.id as string, poi.position, false)),
      ...level.characters.map((character) =>
        target(character.characterId as string, character.position, true),
      ),
    ];
  }

  #reachPx(): number {
    return this.#tuning.interaction?.reachPx ?? 0;
  }

  /**
   * Is this target on offer: can the prompt name it, a tap land on it, the
   * interact key reach it?
   *
   * In reach, as it always was — **or held at by the stop, once it has come
   * into reach** (ADR-0037). A brake can carry a player a little past the edge
   * of reach, and a player who let go to take the prompt must still find it
   * there. The hold ends the way ADR-0032 says, and the offer ends with it.
   * "Once it has come into reach" is what stops a drive caught at its stop
   * line, still hundreds of pixels out, from announcing the offer early.
   */
  #onOffer(id: string, x: number): boolean {
    if (Math.abs(x - this.#state.x) <= this.#reachPx()) return true;
    return id === this.#autoStop.holding && this.#inReach.has(id);
  }

  /** The held subject, when the hold is keeping it on offer. */
  #heldOffer(): string | null {
    const held = this.#autoStop.holding;
    return held !== null && this.#inReach.has(held) ? held : null;
  }

  #updateReach(): void {
    let changed = false;
    for (const target of this.#reachTargets) {
      const near = this.#onOffer(target.id, target.position.x);
      const was = this.#inReach.has(target.id);
      if (near && !was) {
        this.#inReach.add(target.id);
        changed = true;
        this.#emit('poi/entered', target.id);
      } else if (!near && was) {
        this.#inReach.delete(target.id);
        changed = true;
        this.#emit('poi/left', target.id);
      }
    }
    /* The marks are recomputed on a change of reach and on nothing else, which
       is what keeps "what can I tap" free per frame. */
    if (changed) this.#refreshAffordances();
  }

  /**
   * Engage the thing the player asked for, or the nearest thing if they did not
   * name one.
   *
   * A finger names one — it landed on it — and a key does not, so the tapped
   * target wins when there is one. Both still go through the same reach check,
   * so a tap cannot engage something the interact key could not: the strategy
   * has already had its say about `whileMoving` and `requiresStop` by the time
   * this runs, and this is the last gate rather than a second rulebook.
   */
  #engageNearest(): void {
    const asked = this.#pendingEngage;
    this.#pendingEngage = null;

    let best: { id: string; npc: boolean; distance: number } | null = null;
    for (const target of this.#reachTargets) {
      if (!this.#onOffer(target.id, target.position.x)) continue;
      const distance = Math.abs(target.position.x - this.#state.x);
      if (target.id === asked) {
        best = { id: target.id, npc: target.npc, distance };
        break;
      }
      if (best === null || distance < best.distance) {
        best = { id: target.id, npc: target.npc, distance };
      }
    }
    if (best === null) return;
    /*
     * The player is done being stopped here.
     *
     * A drive that halted for this subject lets go now, and neither it nor the
     * subject engaged catches the player again this visit — so the world moves
     * on when the card the engagement opens is closed. The level is paused while
     * it is open, so nothing slides underneath it. This is the resume that
     * matters: a player who chose auto-move because they cannot hold a contact
     * must not need to hold one to leave the first landmark. The interact
     * prompt's route reaches the same call through {@link markEngaged}.
     */
    this.#autoStop.release(best.id);
    this.#backingWatch.release();
    /* The rig has a `once` interact state; firing it is what makes an engagement
       visible in the world rather than only in the DOM above it. A rig that
       declares no such trigger simply has nothing fired at it. */
    const engaged = this.#placed.get(best.id) ?? this.#playerCharacter;
    if (engaged !== null && engaged !== undefined) engaged.fire(INTERACT_TRIGGER);
    this.#emit(best.npc ? 'npc/engaged' : 'poi/engaged', best.id);
    /*
     * Did that engagement pause the level? A card or a dialogue does, from inside
     * the emit, so this frame is the last one drawn until it closes — and the
     * puppet enters `interact` on its rest key. Hold the gesture instead, after
     * this frame's character update (`#holdGesture`). See `engagement-pose.ts`.
     */
    if (engaged !== null && engaged !== undefined && this.sys.isPaused()) {
      const mode = engaged === this.#playerCharacter ? this.#tuning.mode : null;
      const holdMs = gestureHoldMs(this.#options.rig, mode, INTERACT_STATE);
      if (holdMs > 0) this.#heldGesture = { renderer: engaged, holdMs };
    }
  }

  /**
   * Advance an engaged puppet to its gesture, once, on the frame a pause froze.
   *
   * After `#updatePlayerCharacter` and `#updatePlacedCharacters`, because the
   * update that enters `interact` restarts its clock at 0 whatever the frame's
   * delta was; advancing after it lands exactly on the held key.
   */
  #holdGesture(): void {
    const held = this.#heldGesture;
    if (held === null) return;
    this.#heldGesture = null;
    held.renderer.update(held.holdMs);
  }

  /* --------------------------------------------------------------- drawing -- */

  /**
   * Register a drawing that depends on the palette, and run it now.
   *
   * Everything painted from `#palette` goes through here so that the time of day
   * can move without any pass having to know that it can. The closure is called
   * again only when a tinted channel actually changed, which is a handful of
   * times an hour and never on a frame.
   */
  #repaint(draw: () => void): void {
    this.#repaintables.push(draw);
    draw();
  }

  /**
   * A placeholder band's colour: far bands close to the sky, near ones close to
   * the ground.
   *
   * The same aerial perspective an atlas would paint, expressed in two theme
   * colours so the placeholder cannot clash with the level it stands in for —
   * including the level after dusk, which is why it is read from the tinted
   * palette on every repaint rather than fixed at the colour of noon. One method,
   * because the first-row report (ADR-0044) has to name the same colour the band
   * is filled with.
   */
  #placeholderColour(index: number, count: number): number {
    const shade = count <= 1 ? 0.5 : index / (count - 1);
    return blendColors(
      toPhaserColor(this.#palette.sky),
      toPhaserColor(this.#palette.ground),
      0.25 + shade * 0.7,
    );
  }

  /**
   * Tell the renderer what the canvas draws down its sky, if that changed.
   *
   * The page paints the letterbox with it — the band above a letterboxed canvas
   * (ADR-0044) and the desktop side panels. The rule is `sky-top.ts`; this only
   * describes the layers as they stand right now — visible or switched off by
   * the tier, textured or a placeholder, and where the camera has put them.
   *
   * The comparison is on the written-out list, not on the array, because the
   * answer is rebuilt on every camera row and almost always the same: re-applying
   * a dozen unchanged custom properties would be a style recalculation per frame.
   */
  #reportSkyBand(): void {
    this.#skyBandCameraY = this.#camera.y;
    const stops = canvasSkyProfile(
      {
        /* The sky gradient's first band is `mixColor(sky, ground, 0)`, which is
           the tinted sky exactly. */
        backdrop: this.#palette.sky,
        /* ...and every band after it is not. `#paintSky` ramps sky to ground in
           GRADIENT_BANDS steps behind the layers, so what shows through a gap
           between two bands depends on how far down the gap is. */
        backdropAt: (row) => this.#skyGradientAt(row),
        layers: this.#skyTopLayers(),
        cameraY: this.#camera.y,
      },
      { height: this.#options.designHeight, until: this.#skyFloorY() },
    );

    const written = stops.map((stop) => `${String(stop.at)} ${stop.colour}`).join(',');
    if (written === this.#skyBand) return;
    this.#skyBand = written;
    this.#options.onSkyBand?.(stops);
  }

  /**
   * The sky gradient at one canvas row, `#rrggbb` — what shows where no layer draws.
   *
   * The same band arithmetic `#paintSky` fills with, read instead of drawn, so
   * the two cannot disagree: same `GRADIENT_BANDS`, same band index, same
   * `mixColor`. Screen rows, because that graphics object is screen-locked.
   */
  #skyGradientAt(screenRow: number): string {
    const bandHeight = this.#options.designHeight / GRADIENT_BANDS;
    const band = Math.min(
      GRADIENT_BANDS - 1,
      Math.max(0, Math.floor((Number.isFinite(screenRow) ? screenRow : 0) / bandHeight)),
    );
    return toCssColor(
      mixColor(this.#palette.sky, this.#palette.ground, band / (GRADIENT_BANDS - 1)),
    );
  }

  /**
   * Where this level's sky ends, in design pixels: the top of its mid-ground.
   *
   * The backmost layer is the sky; the next one up in depth order is the first
   * thing drawn in front of it — Halifax's citadel at 620, the North's range at
   * 620, Ottawa's skyline at 780 — and that row is where rows stop being flat
   * enough for one colour to describe them. Read from the level document, so a
   * level that re-orders or re-offsets its layers moves its own answer and no
   * engine change is needed (CLAUDE.md: a level is JSON and assets).
   *
   * Capped at the ground crest, because a level whose second band starts below
   * its own ground line has no mid-ground to speak of, and never above 0.
   */
  #skyFloorY(): number {
    const ground = this.#groundCrestY();
    const ordered = [...this.#options.level.layers].sort((a, b) => a.depth - b.depth);
    const mid = ordered
      .slice(1)
      .map((layer) => layer.offset.y)
      .filter((y) => Number.isFinite(y));
    return mid.length === 0 ? ground : Math.max(0, Math.min(ground, Math.min(...mid)));
  }

  /**
   * The first row this level's ground can cover, in design pixels.
   *
   * Where the sky profile stops. Below it the page paints its opaque land band
   * from `levelLandBand`, and the parallax layers this scene composites are no
   * longer what the canvas shows there — `#paintGround` is. The *highest* point
   * of the polyline rather than its mean, because the mean is where the panel's
   * land starts and the highest point is the first row where asking about layers
   * could be wrong. On the nine flat levels the two are the same row.
   */
  #groundCrestY(): number {
    const rows = this.#options.level.ground
      .map((point) => point.y)
      .filter((y) => Number.isFinite(y));
    return rows.length === 0 ? this.#options.designHeight : Math.min(...rows);
  }

  /** Every parallax band as the first row sees it. `#layers` is already in depth order. */
  #skyTopLayers(): SkyTopLayer[] {
    const ordered = [...this.#options.level.layers].sort((a, b) => a.depth - b.depth);
    return this.#layers.flatMap((view, index): SkyTopLayer[] => {
      const layer = ordered.find((candidate) => candidate.key === view.key);
      if (layer === undefined) return [];
      const { scrollFactorY } = view.object as unknown as { readonly scrollFactorY: number };
      if (this.textures.exists(view.key)) {
        return [
          {
            depth: index,
            visible: view.object.visible,
            offsetY: layer.offset.y,
            scrollFactorY,
            height: this.textures.get(view.key).getSourceImage().height,
            rowColour: (row) => this.#textureRowColour(view.key, row),
          },
        ];
      }
      const flat = this.#placeholderColour(ordered.indexOf(layer), ordered.length);
      return [
        {
          depth: index,
          visible: view.object.visible,
          offsetY: layer.offset.y,
          scrollFactorY,
          height: PLACEHOLDER_BAND_HEIGHT,
          rowColour: () => ({ r: (flat >> 16) & 0xff, g: (flat >> 8) & 0xff, b: flat & 0xff, a: 255 }),
        },
      ];
    });
  }

  /**
   * One row of a loaded texture, as one colour. Read once per row, then cached.
   *
   * Through a one-row 2D canvas that is dropped as soon as it is read: no GL
   * texture, so nothing on the decoded-texture budget (ADR-0013), and no GPU
   * readback, so no stalled frame. `null` for a source that cannot be drawn
   * into a canvas, which the caller treats as "look underneath".
   */
  #textureRowColour(key: string, row: number): Rgba | null {
    const cacheKey = `${key}@${String(row)}`;
    const cached = this.#rowColours.get(cacheKey);
    if (cached !== undefined) return cached;

    let colour: Rgba | null = null;
    try {
      const source: unknown = this.textures.get(key).getSourceImage();
      if (isDrawableSource(source) && typeof document !== 'undefined') {
        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = 1;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (context !== null && source.width > 0) {
          context.drawImage(source, 0, row, source.width, 1, 0, 0, source.width, 1);
          /* The median, not the mean: a sky row with clouds on it averages to a
             pale blue that is neither, and a panel built from such averages is a
             smear. On a row that is one colour — every shipped sky's first row,
             which is all ADR-0044 ever read — the two are the same number. */
          colour = medianRowColour(context.getImageData(0, 0, source.width, 1).data);
        }
      }
    } catch (cause) {
      console.warn(`[level] could not read row ${String(row)} of "${key}" for the page's sky band.`, cause);
      colour = null;
    }
    this.#rowColours.set(cacheKey, colour);
    return colour;
  }

  #paintSky(): void {
    const { designWidth, designHeight } = this.#options;
    const graphics = this.add.graphics().setScrollFactor(0, 0).setDepth(this.#depths.sky);
    const bandHeight = designHeight / GRADIENT_BANDS;

    this.#repaint(() => {
      graphics.clear();
      for (let band = 0; band < GRADIENT_BANDS; band += 1) {
        const position = band / (GRADIENT_BANDS - 1);
        graphics.fillStyle(mixColor(this.#palette.sky, this.#palette.ground, position), 1);
        graphics.fillRect(0, Math.floor(band * bandHeight), designWidth, Math.ceil(bandHeight) + 1);
      }
    });
  }

  /**
   * Draw a band between two ordered profiles as quads, one per segment.
   *
   * Not `fillPath`. Phaser triangulates a filled path on the CPU every frame it
   * is drawn, and a level's ground is a static polyline that would be
   * re-triangulated sixty times a second for no benefit. Two triangles per
   * segment is the same picture with no tessellation, and on a software
   * rasteriser — the tier this engine exists to keep playable — it is the
   * difference between hitting the frame budget and missing it.
   */
  #fillUnder(
    graphics: Phaser.GameObjects.Graphics,
    profile: readonly Vec2[],
    bottom: (point: Vec2) => number,
  ): void {
    for (let index = 1; index < profile.length; index += 1) {
      const a = profile[index - 1];
      const b = profile[index];
      if (a === undefined || b === undefined) continue;
      /* Nothing to fill where the band has no height at either end: a flat
         ground whose fill stops at a strip starting on the same row. */
      if (bottom(a) <= a.y && bottom(b) <= b.y) continue;
      graphics.fillTriangle(a.x, a.y, b.x, b.y, a.x, bottom(a));
      graphics.fillTriangle(b.x, b.y, b.x, bottom(b), a.x, bottom(a));
    }
  }

  /**
   * One object per authored layer, textured when the art exists and a flat band
   * when it does not.
   *
   * Both branches read the same five properties out of the document, which is
   * the seam: dropping an atlas in under these keys changes what is drawn and
   * changes nothing about where, how fast or in what order.
   */
  #buildLayers(): void {
    const { level, designWidth } = this.#options;
    const ordered = [...level.layers].sort((a, b) => a.depth - b.depth);
    this.#texturedLayers = 0;

    ordered.forEach((layer, index) => {
      const depth = this.#depths.layer(index);
      if (this.textures.exists(layer.key)) {
        this.#texturedLayers += 1;
        const source = this.textures.get(layer.key).getSourceImage();
        const height = source.height;
        /*
         * Exactly one screen wide — never `level.size.x`, and no longer a screen
         * plus a tile of slack.
         *
         * The slack was over-caution: `tilePositionX` wraps the texture inside
         * the quad, so a quad the width of the viewport shows an unbroken band
         * at every scroll offset and the seam has nowhere to appear. Carrying
         * the extra tile made Ottawa's sky quad 2160 wide and its skyline 2880,
         * for a camera that sees 1080 — vertices, clipping and texture memory
         * for pixels no one can look at.
         */
        const width = layer.repeatX ? designWidth : source.width;
        const sprite = this.add
          .tileSprite(layer.offset.x, layer.offset.y, width, height, layer.key)
          .setOrigin(0, 0)
          .setDepth(depth);
        this.#layers.push({
          key: layer.key,
          object: sprite,
          tiled: layer.repeatX ? { factorX: 1 } : null,
        });
        this.#artBounds.push({
          kind: 'layer',
          rect: {
            x: layer.repeatX ? 0 : layer.offset.x,
            y: layer.offset.y,
            width: layer.repeatX ? level.size.x : source.width,
            height,
          },
        });
        return;
      }

      const band = this.add.graphics().setDepth(depth);
      this.#repaint(() => {
        band.clear();
        band.fillStyle(this.#placeholderColour(index, ordered.length), 1);
        band.fillRect(0, layer.offset.y, level.size.x, PLACEHOLDER_BAND_HEIGHT);
      });
      this.#layers.push({ key: layer.key, object: band, tiled: null });
    });
  }

  /**
   * Apply a parallax rate to one layer.
   *
   * A tiled band keeps its quad still — `scrollFactor.x` of 0 — and remembers
   * the rate for {@link #scrollLayers}. Everything else takes the rate the way
   * it always did. Vertical is unchanged in both: portrait levels move very
   * little vertically and a screen-tall band has no equivalent trick.
   */
  #setLayerRate(layer: LayerView, x: number, y: number): void {
    if (layer.tiled === null) {
      layer.object.setScrollFactor(x, y);
      return;
    }
    layer.tiled.factorX = x;
    layer.object.setScrollFactor(0, y);
  }

  /**
   * Move the contents of every tiled band, once per frame.
   *
   * `tilePositionX = cameraX * rate` is exactly what `scrollFactor.x = rate`
   * did to the quad, applied to the texture instead — so the picture is the
   * same and the cost is one screen rather than the whole level.
   */
  #scrollLayers(): void {
    for (const layer of this.#layers) {
      if (layer.tiled === null) continue;
      (layer.object as Phaser.GameObjects.TileSprite).tilePositionX =
        this.#camera.x * layer.tiled.factorX;
    }
  }

  /**
   * The ground, as the polyline says it is.
   *
   * One filled polygon from the polyline down to the bottom of the world, plus a
   * lit crest along the top in `palette.horizon`. The crest is what makes the
   * ice read as a surface rather than as the top of a block of colour, and it
   * follows the polyline, so it cannot be mistaken for a rule across the screen
   * (see `boot-scene.ts` for why that matters).
   */
  #paintGround(): void {
    const { level } = this.#options;
    const ground = this.add.graphics().setDepth(this.#depths.ground);

    this.#repaint(() => {
      ground.clear();
      /* `GROUND_FILL_SHADE`, not a literal: `GameRenderer.cssVariables()` paints
         the desktop side panels with this exact colour, and a second copy of the
         number is a seam at the letterbox edge waiting to happen. */
      ground.fillStyle(
        blendColors(toPhaserColor(this.#palette.ground), 0x000000, GROUND_FILL_SHADE),
        1,
      );
      /* Down to the ground dressing, when one drew to the bottom of the world:
         the fill below it would be covered in the same frame (ADR-0042). */
      const floor = this.#groundFloor();
      this.#fillUnder(ground, level.ground, () => floor);

      ground.lineStyle(6, toPhaserColor(this.#palette.horizon), 0.9);
      ground.beginPath();
      level.ground.forEach((point, index) => {
        if (index === 0) ground.moveTo(point.x, point.y);
        else ground.lineTo(point.x, point.y);
      });
      ground.strokePath();
    });
  }

  /**
   * The level's ground dressing (ADR-0042): the strip over the ground fill.
   *
   * One screen wide and fixed to the world through its tile offset, as a
   * repeating band and a ride's track are, so it costs one quad however long the
   * level is. Not given to the tier: every tier draws it, because the flat band
   * it dresses is the defect a dropped strip would put back. A strip placed above
   * the ground's lowest point never reaches the scene (the parser refuses it), so
   * the one failure left here is art that did not load — a sentence, and
   * `data-ground-dressing-drawn` false, rather than a flat band that looks meant.
   */
  #paintGroundDressing(): void {
    const { level, designWidth } = this.#options;
    const dressing = level.groundDressing;
    if (!this.textures.exists(dressing.key)) {
      console.error(
        `[level] the ground dressing "${dressing.key}" has no texture, so the band below the ` +
          'walking line is flat fill (ADR-0042).',
      );
      return;
    }
    const source = this.textures.get(dressing.key).getSourceImage();
    this.#groundDressing = this.add
      .tileSprite(0, dressing.topY, designWidth, source.height, dressing.key)
      .setOrigin(0, 0)
      .setScrollFactor(0, 1)
      .setDepth(this.#depths.groundDressing);
  }

  /**
   * How far down the ground fill and its sheen are painted: to the strip's first
   * row when the strip's art loaded and reaches the bottom of the world, and to
   * the bottom of the world otherwise (`groundFillFloor`). Asked of the texture
   * manager rather than of `#groundDressing`, because the fill is painted before
   * the strip is built.
   */
  #groundFloor(): number {
    const { level } = this.#options;
    const { key } = level.groundDressing;
    const stripHeight = this.textures.exists(key) ? this.textures.get(key).getSourceImage().height : null;
    return groundFillFloor({ dressing: level.groundDressing, stripHeight, worldHeight: level.size.y });
  }

  /** Move the strip's contents with the world, once per frame. It has no parallax rate of its own. */
  #scrollGroundDressing(): void {
    if (this.#groundDressing === null) return;
    this.#groundDressing.tilePositionX = this.#camera.x;
  }

  /**
   * The two tier-gated overlays: a pale sheen along the ground and a band of
   * light over the backdrop.
   *
   * Drawn once and left alone; only their alpha and visibility are touched, by
   * the effects, from `applyProfile`. Both are shallow bands rather than screen
   * fills, so the pair costs well under a third of a screen against CLAUDE.md's
   * 4x overdraw budget.
   */
  #paintOverlays(): void {
    const { level, designWidth, designHeight } = this.#options;

    const sheen = this.add.graphics().setDepth(this.#depths.sheen);
    this.#repaint(() => {
      sheen.clear();
      sheen.fillStyle(toPhaserColor(this.#palette.horizon), 1);
      const floor = this.#groundFloor();
      this.#fillUnder(sheen, level.ground, (point) => Math.min(point.y + SHEEN_DEPTH, floor));
    });
    this.#sheen = sheen;

    const haze = this.add.graphics().setScrollFactor(0, 0).setDepth(this.#depths.haze);
    this.#repaint(() => {
      haze.clear();
      haze.fillStyle(toPhaserColor(this.#palette.horizon), 1);
      haze.fillRect(0, designHeight * HAZE_TOP_FRACTION, designWidth, HAZE_HEIGHT);
    });
    this.#haze = haze;
  }

  #paintPois(): void {
    const { level } = this.#options;
    for (const poi of level.pois) {
      const y = groundYAt(level.ground, poi.position.x);
      if (this.textures.exists(poi.artKey)) {
        const image = this.add
          .image(poi.position.x, y, poi.artKey)
          .setOrigin(0.5, 1)
          .setDepth(this.#depths.poi);
        this.#actorsDrawn += 1;
        const rect = {
          x: poi.position.x - image.width / 2,
          y: y - image.height,
          width: image.width,
          height: image.height,
        };
        this.#drawnRects.set(poi.id as string, rect);
        const art = this.#textureSilhouette(poi.artKey, rect);
        if (art !== null) this.#drawnArt.set(poi.id as string, art);
        this.#artBounds.push({ kind: 'actor', rect });
        continue;
      }
      /* A tower silhouette: tall enough to be framed by TN-LEVEL-04's "fully
         inside the canvas" check, plain enough that nobody files it as art. */
      const mark = this.add.graphics().setDepth(this.#depths.poi);
      this.#repaint(() => {
        mark.clear();
        mark.fillStyle(blendColors(toPhaserColor(this.#palette.horizon), 0x000000, 0.35), 1);
        mark.fillRect(poi.position.x - 90, y - 620, 180, 620);
        mark.fillTriangle(
          poi.position.x - 110,
          y - 620,
          poi.position.x + 110,
          y - 620,
          poi.position.x,
          y - 780,
        );
      });
      /* The placeholder is tappable too. A level whose art has not been packed
         is still a level somebody has to be able to play with a finger. */
      this.#drawnRects.set(poi.id as string, {
        x: poi.position.x - 110,
        y: y - 780,
        width: 220,
        height: 780,
      });
    }
  }

  /**
   * The level's characters, composed from the rig.
   *
   * This drew a rounded rectangle for every character in the game, in
   * production, which meant the red serge — the reference-critical uniform that
   * was the stated reason Ottawa is the first level — was never on screen. The
   * renderer had been written and measured and nothing constructed it.
   *
   * The placeholder is kept for the case it was always for: no rig, or an atlas
   * that has not been packed. What is new is that the two are told apart —
   * `#actorsDrawn` counts the ones that composed from real art, the probe
   * publishes it, and **every fallback prints why** — so "drew the officer" and
   * "drew a rectangle" stop being the same observation.
   */
  #paintCharacters(): void {
    const { level } = this.#options;
    for (const [index, character] of level.characters.entries()) {
      const id = String(character.characterId);
      const y = groundYAt(level.ground, character.position.x);
      /* Its own slot, in the order the level lists characters: no two
         characters' parts share a depth, so nobody can be drawn between
         somebody else's head and hat (`depth-plan.ts`). */
      const depth = this.#characterDepth(index);
      const renderer = this.#composeCharacter(
        id,
        artboardFor(this.#options.rig, id),
        {},
        depth,
        null,
      );

      if (renderer !== null) {
        renderer.setFacing(character.facing);
        renderer.setPosition(character.position.x, y);
        /* An NPC stands still and talks when engaged; the rig's own inputs say
           so, and the scene sets them by name rather than choosing a state. */
        this.#driveCharacter(renderer, { grounded: true, moving: false, speed: 0, verticalSpeed: 0 });
        renderer.update(0);
        this.#characters.push(renderer);
        this.#placed.set(id, renderer);
        this.#actorsDrawn += 1;
        const rect = this.#characterRect(character.position.x, y);
        if (rect !== null) {
          this.#drawnRects.set(id, rect);
          this.#artBounds.push({ kind: 'actor', rect });
        }
        const rig = this.#options.rig;
        const board = artboardFor(rig, id);
        const art =
          rig === null || rig === undefined || board === null
            ? null
            : figureSilhouette(rig, board.artboard, {
                x: character.position.x,
                groundY: y,
                facing: character.facing,
              });
        if (art !== null) this.#drawnArt.set(id, art);
        continue;
      }

      const actor = this.add.graphics().setDepth(depth);
      actor.fillStyle(blendColors(toPhaserColor(level.palette.ink), 0xffffff, 0.15), 1);
      actor.fillRoundedRect(
        character.position.x - ACTOR_WIDTH / 2,
        y - ACTOR_HEIGHT,
        ACTOR_WIDTH,
        ACTOR_HEIGHT,
        18,
      );
      this.#drawnRects.set(id, {
        x: character.position.x - ACTOR_WIDTH / 2,
        y: y - ACTOR_HEIGHT,
        width: ACTOR_WIDTH,
        height: ACTOR_HEIGHT,
      });
    }
  }

  /**
   * The player, composed from the rig — which is the whole of the "the character
   * is still a rectangle" defect.
   *
   * Every diagnosis of that bug had looked at `#paintCharacters`, because that
   * is where the rig work went. The player was never in it: they are not in
   * `level.characters`, so they were not in `data-actors` either, and the
   * counter added specifically to stop a placeholder shipping unnoticed **could
   * not see the one character on screen at the spawn**. It was not a lookup
   * failing quietly; nothing had ever asked.
   *
   * Which artboard is the player's is a fact about the rig rather than a name in
   * this file — see `character-cast.ts` — so a second playable character is a
   * rig edit and not an engine one.
   */
  #paintPlayer(): void {
    const { level } = this.#options;
    const artboard = playerArtboard(this.#options.rig);
    const renderer = this.#composeCharacter(
      PLAYER_SUBJECT,
      artboard,
      /* The level dresses the player (`playerCostume`, from its art sheet's
         season) and the creator chooses everything else. `costume` is not a
         slot the creator offers, so the two never compete for one key. */
      { ...(this.#options.playerSkins ?? {}), costume: level.playerCostume },
      /* The slot above every other character's, so the player is drawn whole
         and in front of anybody they stop on. */
      this.#depths.player.baseDepth,
      /*
       * **The line the whole defect was.** The level document declares how the
       * player moves here and the rig is now told, so a level that says skating
       * poses a skater. Nothing else in this file knows which mode that is: the
       * name comes from the document, the pose comes from the rig, and the join
       * is `locomotion-pose.ts`.
       */
      this.#tuning.mode,
    );

    if (renderer !== null) {
      this.#playerCharacter = renderer;
      renderer.setFacing(this.#state.facing);
      renderer.setPosition(this.#state.x, this.#state.y);
      this.#driveCharacter(renderer, {
        grounded: this.#state.grounded,
        moving: false,
        speed: 0,
        verticalSpeed: 0,
      });
      renderer.update(0);
      return;
    }

    const player = this.add.graphics().setDepth(this.#depths.player.baseDepth);
    player.fillStyle(toPhaserColor(level.palette.ink), 1);
    player.fillRoundedRect(-ACTOR_WIDTH / 2, -ACTOR_HEIGHT, ACTOR_WIDTH, ACTOR_HEIGHT, 18);
    player.setPosition(this.#state.x, this.#state.y);
    this.#player = player;
  }

  /**
   * The ride for the mode in force, if the level declares one (ADR-0031).
   *
   * A layer whose texture did not load is skipped and **said**, like every other
   * fallback in this file, and `data-rides-drawn` then reads 0 against
   * `data-rides` 1 — because the failure it stands for is a rider posed in a seat
   * that is not on screen, and that reaches `ready` exactly like success.
   *
   * Depths are the player's slot's two empty ends (`depth-plan.ts`): `behind`
   * under the rider's first part, `front` over the last, both over every other
   * character — a ride runs on a nearer line than the people the level places —
   * and the track on the ground under every actor. The slot is sized from the
   * rig's `z` range, so a rig that grows a part does not end up in front of the
   * car.
   */
  #paintRide(): void {
    const ride = this.#ride;
    if (ride === null) return;

    const sizeOf = (key: string): RideArtSize | null => {
      if (!this.textures.exists(key)) return null;
      const source = this.textures.get(key).getSourceImage();
      return { width: source.width, height: source.height };
    };
    const problems = rideArtProblems(ride, sizeOf);
    for (const problem of problems) console.error(`[level] ${problem}`);

    for (const layer of ride.art) {
      const size = sizeOf(layer.key);
      if (size === null) continue;
      this.#rideSize ??= size;
      const depth = layer.side === 'behind' ? this.#depths.rideBehind : this.#depths.rideFront;
      const object = this.add.image(0, 0, layer.key).setOrigin(0.5, 0).setDepth(depth);
      this.#rideArt.push({ object, layer, shown: layer.key });
    }

    /* Where the ride's art is while it stands — its rest frame, and the still
       reduced motion holds — so a stop never rests it across somebody's feet
       (ADR-0049, `stand-off.ts`). One band per column: a foot is narrow. */
    const standing = new Set(
      ride.art.flatMap((layer) => [layer.key, ...(layer.cycle === undefined ? [] : [layer.cycle.rest])]),
    );
    this.#rideSilhouettes = [...standing].flatMap((key) => {
      const read = this.#textureLocalSilhouette(key, 1);
      return read === null ? [] : [read.art];
    });

    if (ride.track !== undefined) {
      const size = sizeOf(ride.track.artKey);
      if (size !== null) {
        this.#rideTrack = this.add
          .tileSprite(0, 0, this.#options.designWidth, size.height, ride.track.artKey)
          .setOrigin(0, 0)
          .setScrollFactor(0, 1)
          .setDepth(this.#depths.rideTrack);
      }
    }

    this.#ridesDrawn = problems.length === 0 ? 1 : 0;
  }

  /**
   * Put the ride, its track and its rider where they are this frame.
   *
   * The arithmetic is `ride.ts`'s. What is here is the frame's inputs: distance
   * travelled for the rock and the gait, speed as a fraction of cruise, reduced
   * motion from the tier, and any height the rider has off the ground, so a ride
   * that can jump rises with its rider instead of leaving them in the air.
   *
   * A layer's frame is set only when it changes, and only to a texture that
   * loaded: a frame that did not is already a sentence and `data-rides-drawn` 0
   * (`rideArtProblems`), and swapping to a missing key would draw Phaser's
   * missing-texture square where the horse was.
   */
  #placeRide(dtSeconds: number): void {
    const ride = this.#ride;
    const size = this.#rideSize;
    if (ride === null || size === null) {
      this.#riderY = this.#state.y;
      return;
    }
    const { level } = this.#options;
    this.#rideDistancePx += Math.abs(this.#state.velocityX) * dtSeconds;
    const groundY = groundYAt(level.ground, this.#state.x);
    const reducedMotion = this.#profile?.motion === 'reduced';
    const bob = rideBobPx({
      bob: ride.bob,
      distancePx: this.#rideDistancePx,
      speedFraction: Math.abs(this.#state.velocityX) / this.#tuning.maxSpeed,
      reducedMotion,
    });
    const placement = ridePlacement({
      ride,
      size,
      riderX: this.#state.x,
      groundY,
      facing: this.#state.facing,
      bobPx: bob + (this.#state.y - groundY),
    });
    for (const layer of this.#rideArt) {
      const key = rideFrameKey({
        layer: layer.layer,
        distancePx: this.#rideDistancePx,
        speed: this.#state.velocityX,
        reducedMotion,
      });
      if (key !== layer.shown && this.textures.exists(key)) {
        layer.object.setTexture(key);
        layer.shown = key;
      }
      layer.object.setPosition(placement.centreX, placement.top);
      layer.object.setFlipX(placement.flipX);
    }
    if (this.#rideTrack !== null && placement.trackTop !== null) {
      this.#rideTrack.setY(placement.trackTop);
      this.#rideTrack.tilePositionX = this.#camera.x;
    }
    this.#riderY = placement.riderY;
  }

  /**
   * One character renderer, or `null` **and a line saying why**.
   *
   * Constructed here, synchronously, because the sprite backend draws *into this
   * scene* and cannot exist before one does, and because `create` is called from
   * `create()` where there is no frame to await in.
   *
   * That is why `createSpriteCharacterRenderer` is called rather than the port's
   * `CharacterRendererFactory.create`: the port is async because a Rive file has
   * to load, and awaiting a promise that is already settled would still cost a
   * microtask and leave the first frame with no character on it. The factory
   * remains the seam bootstrap will pick a backend through — when Rive has an
   * artboard with anything drawn in it, the load moves into `loadLevel` beside
   * the level document and this call site takes the result. **Reported, so it is
   * not discovered:** until then the scene names one backend, which is a
   * deviation from ADR-0005's "bootstrap is the only place concretes are wired"
   * that the measurement (sprite ships, Rive undecided) makes moot rather than
   * resolves.
   *
   * Every `null` below used to be silent. A fallback nobody can observe becomes
   * permanent — that is how a level shipped with no art in it and how every
   * character in the game shipped as a rectangle — so each one now names the
   * subject and the missing thing on the console, in every build.
   */
  #composeCharacter(
    subject: string,
    artboard: RigArtboard | null,
    skins: Readonly<Record<string, string>>,
    depth: number,
    /**
     * How this character gets about, or `null` for one that does not.
     *
     * The player is handed the level's declared mode and nobody else is, which
     * is a decision and not an omission: an NPC on the ice is *standing* on it.
     * A level that wants a skating NPC says so by placing one, which is a
     * document change and a rig pose, not an engine change.
     */
    mode: string | null,
  ): SpriteCharacterRenderer | null {
    const rig = this.#options.rig ?? null;
    if (rig === null) return this.#reportCastGap(subject, 'no-rig');
    if (artboard === null) return this.#reportCastGap(subject, 'no-artboard');

    const atlas = this.#rigAtlas(rig);
    if (atlas === null) return this.#reportCastGap(subject, 'no-atlas');

    const spec: CharacterRendererSpec = {
      characterId: artboard.characterId,
      artboard: artboard.artboard,
      stateMachine: artboard.stateMachine,
      rig,
      skins,
      widthPx: rig.characterSpace.width,
      heightPx: rig.characterSpace.height,
    };

    /* Every part this character is dressed in, including any a later re-dress
       makes, so `data-parts-interleaved` is read off the display list rather
       than restated from the plan. */
    const parts: Phaser.GameObjects.Image[] = [];
    const built = createSpriteCharacterRenderer(spec, {
      textureKey: atlas,
      frames: {
        hasFrame: (key, frame) => this.textures.exists(key) && this.textures.get(key).has(frame),
      },
      host: {
        /* `__DEFAULT` is Phaser's built-in blank texture: the renderer sets the
           real frame on the next line of its own constructor, and an image needs
           *a* texture to exist at all. */
        createPart: (): SpritePartObject => {
          const part = this.add.image(0, 0, '__DEFAULT');
          parts.push(part);
          return part;
        },
      },
      baseDepth: depth,
      /* Spread rather than `mode: mode ?? undefined`: `exactOptionalPropertyTypes`
         makes an explicit `undefined` a different thing from an absent key, and
         "this character has no mode" is the absent key. */
      ...(mode === null ? {} : { mode }),
    });
    if (built.ok) {
      this.#partGroups.push({ owner: subject, parts });
      return built.value;
    }
    /* The half-packed atlas gets the sentence written for it; anything else —
       an appearance naming an option the rig dropped, a rig with no parts — is
       reported as itself rather than dressed up as a missing atlas. */
    if (built.error.code === 'character.atlas.noParts') {
      return this.#reportCastGap(subject, 'no-parts');
    }
    console.error(
      `[level] "${subject}" is drawn as a placeholder: ${built.error.code}: ${built.error.message}`,
    );
    this.#placeholders += 1;
    return null;
  }

  /**
   * The base depth of the `index`-th character the level places.
   *
   * The plan was built from this level's own character list, so a missing band
   * means the scene is painting a list it was not constructed with — refused
   * rather than guessed, because a guess is exactly how two puppets end up in
   * one slot.
   */
  #characterDepth(index: number): number {
    const band = this.#depths.characters[index];
    if (band === undefined) {
      throw new TypeError(
        `character ${String(index)} has no depth slot: the plan has ` +
          `${String(this.#depths.characters.length)}. The scene is painting a different ` +
          'character list from the one it was constructed with.',
      );
    }
    return band.baseDepth;
  }

  /**
   * Drawn things that sit, by depth, between the first and last part of a
   * character they are not part of — `data-parts-interleaved`, 0 when healthy.
   *
   * Read off the display list as it is, not restated from `#depths`, so a depth
   * set anywhere else in this file is counted too. Asked once, at ready: every
   * depth in the level is fixed by then.
   */
  #partsInterleaved(): number {
    const owned = new Set<Phaser.GameObjects.GameObject>();
    const groups: DepthGroup[] = this.#partGroups.map(({ owner, parts }) => {
      const live = parts.filter((part) => this.children.exists(part));
      for (const part of live) owned.add(part);
      return { owner, depths: live.map((part) => part.depth) };
    });
    const loose = this.children.list
      .filter((object) => !owned.has(object))
      .map((object) => ('depth' in object && typeof object.depth === 'number' ? object.depth : 0));
    return interleavedDepths(groups, loose);
  }

  /**
   * Which loaded texture the rig was packed into, worked out once per level.
   *
   * The lookup is every loaded key against every frame the rig declares, and the
   * answer is the same for every character in the level — so asking it per
   * character was a few thousand texture-manager calls at load for one fact.
   */
  #rigAtlas(rig: RigDocument): string | null {
    if (this.#atlasKey === undefined) {
      this.#atlasKey = rigAtlasKey(rig, this.textures.getTextureKeys(), (key, frame) =>
        this.textures.exists(key) ? this.textures.get(key).has(frame) : false,
      );
    }
    return this.#atlasKey;
  }

  /** Print the gap and answer `null`, so a call site is one line and never silent. */
  #reportCastGap(subject: string, gap: CastGap): null {
    console.error(`[level] ${castGapMessage(subject, gap)}`);
    this.#placeholders += 1;
    return null;
  }

  /**
   * What the player's rig is being drawn as, for the probe.
   *
   * Three fields, and the split is the point. `data-mode` is what the level
   * declares and what the HUD names; `data-character-mode` is what the puppet
   * was built for; `data-pose` is the timeline it is playing this frame. The
   * defect was the first disagreeing with the second in every level in the game,
   * so a scenario has to be able to read both — and `data-mode-gaps` says
   * whether the third is the mode's own pose or the rig's base cycle standing in
   * for art that has not landed.
   *
   * Omitted entirely while the player is a placeholder: there is no rig to ask,
   * and `unknown` is the honest answer the probe already has for that.
   */
  #characterModeSnapshot(): {
    readonly characterMode?: string;
    readonly pose?: string;
    readonly modeGaps: number;
  } {
    const player = this.#playerCharacter;
    const gaps = { modeGaps: this.#modeGaps.length };
    if (player === null) return gaps;
    return {
      ...gaps,
      ...(player.mode === null ? {} : { characterMode: player.mode }),
      pose: player.pose,
    };
  }

  /**
   * Where a landmark's art is inside its rectangle, read from its texture's alpha
   * once, when the level is built (ADR-0049).
   *
   * The region read is exactly what `add.image(x, y, key)` draws: the texture's
   * base frame. Through a 2D canvas that is dropped as soon as it is read, like
   * `#textureRowColour`: no GL texture, so nothing on the decoded-texture budget,
   * and no GPU readback. `null` for a source that cannot be drawn into a canvas,
   * and the mark then goes over the rectangle, as it always did.
   */
  #textureSilhouette(key: string, rect: TargetRect): ArtSilhouette | null {
    const read = this.#textureLocalSilhouette(key);
    return read === null
      ? null
      : placeSilhouette(read.art, { x: rect.x, y: rect.y, scale: rect.width / read.width });
  }

  /** A texture's silhouette in its own pixels, with the width it was read at, or `null`. */
  #textureLocalSilhouette(
    key: string,
    step?: number,
  ): { readonly art: ArtSilhouette; readonly width: number } | null {
    if (!this.textures.exists(key)) return null;
    try {
      const texture = this.textures.get(key);
      const source: unknown = texture.getSourceImage();
      const frame = texture.get();
      if (!isDrawableSource(source) || typeof document === 'undefined') return null;
      const width = Math.round(frame.cutWidth);
      const height = Math.round(frame.cutHeight);
      if (width <= 0 || height <= 0) return null;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (context === null) return null;
      context.drawImage(source, frame.cutX, frame.cutY, width, height, 0, 0, width, height);
      const art = silhouetteFromRgba(
        context.getImageData(0, 0, width, height).data,
        width,
        height,
        step === undefined ? {} : { step },
      );
      canvas.width = 0;
      canvas.height = 0;
      return art === null ? null : { art, width };
    } catch (cause) {
      console.warn(`[level] could not read where "${key}" is drawn; marks and stops fall back to its rectangle.`, cause);
      return null;
    }
  }

  /** Where a rig-composed character stands, in world space. */
  #characterRect(x: number, groundY: number): TargetRect | null {
    const space = this.#options.rig?.characterSpace;
    if (space === undefined) return null;
    return {
      x: x - space.centreX,
      y: groundY - space.soleY,
      width: space.width,
      height: space.height,
    };
  }

  /**
   * Set the rig inputs this scene can compute, and only the ones the rig
   * declares.
   *
   * Filtered rather than attempted: `setBool`/`setNumber` answer an undeclared
   * input with a `Result` error, and a per-frame caller cannot usefully read
   * one — it would either be dropped (silent, again) or logged sixty times a
   * second. `character-cast.ts` intersects what the scene can compute with what
   * the rig declares, once, at create.
   */
  #driveCharacter(
    renderer: ICharacterRenderer,
    signals: {
      readonly grounded: boolean;
      readonly moving: boolean;
      readonly speed: number;
      readonly verticalSpeed: number;
      readonly talking?: boolean;
    },
  ): void {
    const reduced = this.#profile?.motion === 'reduced';
    for (const name of this.#driven.bools) {
      const value =
        name === 'grounded'
          ? signals.grounded
          : name === 'moving'
            ? signals.moving
            : name === 'talking'
              ? (signals.talking ?? false)
              : reduced;
      renderer.setBool(name, value);
    }
    for (const name of this.#driven.numbers) {
      renderer.setNumber(name, name === 'speed' ? signals.speed : signals.verticalSpeed);
    }
  }

  /* --------------------------------------------------- per-frame character --- */

  /**
   * The player's pose, from the same step the physics just produced.
   *
   * `animationSpeed` is the strategy's own normalised 0..1 — the number
   * `LocomotionAnimationBinding.speedInput` exists to carry — so walking and
   * skating drive the rig identically and differ only in the tuning that
   * produced the number. `verticalSpeed` is negated because screen y grows
   * downwards and the rig's `jump-rise` rule reads "verticalSpeed > 0".
   *
   * Order matters: inputs, then anchor, then `update`. `setPosition` repaints
   * only when the anchor actually moved and `update` repaints once, so a
   * standing character costs one paint per frame and a moving one costs two.
   */
  #updatePlayerCharacter(animationSpeed: number, deltaMs: number): void {
    const player = this.#playerCharacter;
    if (player === null) return;
    this.#driveCharacter(player, {
      grounded: this.#state.grounded,
      moving: Math.abs(this.#state.velocityX) > MOVING_THRESHOLD_PX_S,
      speed: animationSpeed,
      verticalSpeed: -this.#state.velocityY,
    });
    player.setFacing(this.#state.facing);
    /* `#riderY`, which is the physics y unless a ride seats the player higher
       or lower (ADR-0031); `#placeRide` has already run this frame. */
    player.setPosition(this.#state.x, this.#riderY);
    player.update(deltaMs);
  }

  /**
   * Placed characters, skipped while they are off screen.
   *
   * A puppet's `update` interpolates and re-places twenty parts. Doing that for
   * a character 4 000 design pixels away buys nothing a player can see, and this
   * is the frame budget the perf suite now only *reports* on — so the saving is
   * taken here rather than assumed to be affordable. A character that comes back
   * into view is repainted on the frame it does, because `update` re-derives the
   * pose from elapsed time rather than accumulating it.
   */
  #updatePlacedCharacters(deltaMs: number): void {
    if (this.#characters.length === 0) return;
    const view = this.#cameraRect();
    for (const [id, character] of this.#placed) {
      const rect = this.#drawnRects.get(id);
      if (rect !== undefined && !intersectsView(rect, view)) continue;
      character.update(deltaMs);
    }
  }

  /** The camera's world rectangle right now. */
  #cameraRect(): WorldRect {
    const { level, designWidth, designHeight } = this.#options;
    const scale = this.#backingScale();
    return cameraView(
      this.#camera,
      { width: designWidth * scale, height: designHeight * scale },
      { ...level.camera, zoom: level.camera.zoom * scale },
    );
  }

  /* ------------------------------------------------------------ affordance --- */

  /**
   * Put a mark over everything a finger can engage.
   *
   * The user's report was "we don't know what to click", and it was exactly
   * right: the tap rules worked, the reach rules worked, and nothing on screen
   * said that a person or a landmark was any more touchable than the sky. What
   * is drawn here is the missing half of a control that already existed.
   *
   * The **non-visual equivalent** is already in place and is not duplicated
   * here: the canvas is `aria-hidden`, `poi/entered` and `poi/left` are emitted
   * as reach changes, and `app/ui` speaks them through the live region. What is
   * still missing is the *wording* for a prompt — `hud.interact.*` does not
   * exist — and inventing player-facing copy in an adapter is not this
   * directory's to do (ADR-0010). The keys needed are named in the task report.
   */
  #buildAffordances(): void {
    /*
     * One repaintable for the whole set, not one per mark.
     *
     * A closure per mark would capture an object the next refresh may have
     * destroyed, and `#repaintables` would grow every time the set changed —
     * a leak whose symptom is a draw call on a dead Graphics at dusk. One
     * closure over the live map has neither problem.
     */
    this.#repaint(() => {
      for (const entry of this.#marks.values()) this.#drawMark(entry.object, entry.mark);
    });
    this.#refreshAffordances();
  }

  /** Everything engageable, as the affordance module wants it. */
  #affordanceSubjects(): readonly AffordanceSubject[] {
    return this.#reachTargets.map((target) => ({
      id: target.id,
      npc: target.npc,
      position: target.position,
      rect: target.rect,
      art: target.art,
      clear: target.clear,
    }));
  }

  /**
   * Recompute every mark's state and redraw the ones that changed.
   *
   * Called when reach changes and when the domain reports something finished —
   * not every frame. The only per-frame work an affordance costs is
   * {@link #updateAffordances}, which is a scale and a visibility flag.
   */
  #refreshAffordances(): void {
    const marks = affordanceMarks(this.#affordanceSubjects(), {
      playerX: this.#state.x,
      reachPx: this.#reachPx(),
      minTouchPx: this.#minTouchWorldPx(),
      completed: this.#completed,
      held: this.#heldOffer(),
    });

    const live = new Set(marks.map((mark) => mark.id));
    for (const [id, entry] of this.#marks) {
      if (!live.has(id)) {
        entry.object.destroy();
        this.#marks.delete(id);
      }
    }

    for (const mark of marks) {
      const existing = this.#marks.get(mark.id);
      if (existing === undefined) {
        /* Above the last character slot, deliberately: a mark hidden behind the
           thing it points at is the defect it exists to fix. */
        const object = this.add.graphics().setDepth(this.#depths.affordance);
        object.setPosition(mark.x, mark.y);
        const entry = { object, mark };
        this.#marks.set(mark.id, entry);
        this.#drawMark(object, mark);
        continue;
      }
      if (existing.mark.state === mark.state && existing.mark.size === mark.size) {
        existing.mark = mark;
        existing.object.setPosition(mark.x, mark.y);
        continue;
      }
      existing.mark = mark;
      existing.object.setPosition(mark.x, mark.y);
      this.#drawMark(existing.object, mark);
    }
  }

  /**
   * One mark, as three shapes that differ in **shape**.
   *
   * `idle` is an empty ring, `ready` adds a filled centre and a chevron pointing
   * down at the subject, `done` replaces the centre with a stamp. CLAUDE.md:
   * colour is never the only signal, and a player who cannot separate the two
   * ring colours can still separate a ring from a ring with an arrow under it.
   *
   * A dark halo is stroked under every one of them so the mark reads against a
   * bright sky and a dark landmark alike, which is the same problem the high
   * contrast requirement names.
   */
  #drawMark(object: Phaser.GameObjects.Graphics, mark: AffordanceMark): void {
    const radius = mark.size * MARK_RING_FRACTION;
    const bright = blendColors(toPhaserColor(this.#palette.horizon), 0xffffff, 0.3);
    const muted = toPhaserColor(this.#palette.inkMuted);
    const halo = toPhaserColor(this.#palette.ink);

    object.clear();
    object.lineStyle(10, halo, 0.3);
    object.strokeCircle(0, 0, radius);

    if (mark.state === 'idle') {
      object.lineStyle(5, bright, 0.7);
      object.strokeCircle(0, 0, radius);
      object.setScale(1);
      return;
    }
    if (mark.state === 'done') {
      object.lineStyle(5, muted, 0.9);
      object.strokeCircle(0, 0, radius);
      object.fillStyle(muted, 0.95);
      object.fillRect(-radius * 0.42, -radius * 0.42, radius * 0.84, radius * 0.84);
      object.setScale(1);
      return;
    }

    object.lineStyle(7, bright, 1);
    object.strokeCircle(0, 0, radius);
    object.fillStyle(bright, 1);
    object.fillCircle(0, 0, radius * 0.34);
    /* The chevron points at the thing, so a mark over a crowd is unambiguous. */
    object.fillTriangle(
      -radius * 0.5,
      radius * 0.78,
      radius * 0.5,
      radius * 0.78,
      0,
      mark.size * MARK_TIP_FRACTION,
    );
  }

  /**
   * The only per-frame cost an affordance has: a scale and a visibility flag.
   *
   * The pulse is `markPulse`, which returns exactly 1 under reduced motion — so
   * reduced motion is a branch that does nothing rather than a slower animation
   * (CLAUDE.md). Marks outside the camera are hidden rather than scaled, which
   * also keeps them out of the overdraw budget.
   */
  #updateAffordances(): void {
    if (this.#marks.size === 0) return;
    const view = this.#cameraRect();
    const reduced = this.#profile?.motion === 'reduced';
    const pulse = markPulse(this.#elapsedMs, reduced);

    for (const entry of this.#marks.values()) {
      /* Shown only while the column it points at is on screen: half a ring at the
         edge of the glass, pointing at something off it, is a mark for nothing. */
      const anchor = markAnchor(entry.mark);
      const visible =
        anchor.x >= view.x &&
        anchor.x <= view.x + view.width &&
        intersectsView(markExtent(entry.mark), view);
      entry.object.setVisible(visible);
      if (!visible) continue;
      if (entry.mark.state === 'ready') entry.object.setScale(pulse);
    }
  }

  /**
   * How many marks on screen overlap a character this frame: the player's
   * standing figure where they stand, or a placed character's art. Probe only —
   * called behind `?e2e=1` — and zero while the player is off the ground, where
   * a jump may pass through a mark. `mark-clearance.ts` is why it is zero.
   */
  #marksOnActors(): number {
    const figure = this.#playerFigure;
    const player: TargetRect | null =
      figure === null || !this.#state.grounded
        ? null
        : { x: this.#state.x + figure.x, y: this.#riderY + figure.y, width: figure.width, height: figure.height };
    const boxes = player === null ? this.#actorBoxes : [player, ...this.#actorBoxes];
    let count = 0;
    for (const entry of this.#marks.values()) {
      if (!entry.object.visible) continue;
      const extent = markExtent(entry.mark);
      const hit = boxes.some(
        (box) =>
          extent.x < box.x + box.width &&
          box.x < extent.x + extent.width &&
          extent.y < box.y + box.height &&
          box.y < extent.y + extent.height,
      );
      if (hit) count += 1;
    }
    return count;
  }

  /** How many things are advertised as tappable, and how many are in reach. */
  #affordanceCounts(): { affordances: number; affordancesReady: number } {
    let ready = 0;
    for (const entry of this.#marks.values()) {
      if (entry.mark.state === 'ready') ready += 1;
    }
    return { affordances: this.#marks.size, affordancesReady: ready };
  }

  /* ------------------------------------------------------------ milestones --- */

  /**
   * The player engaged this subject by a route that did not pass through this
   * scene — the interact prompt, by touch, by `Tab` and `Enter`, or by the switch.
   *
   * `#engageNearest` already tells the auto-stop about the interact key and a
   * tap on the canvas. This is the same call for the prompt, so a player the
   * level brought to rest at a landmark is let go however they chose it, and is
   * not caught by it again this visit (ADR-0032). Idempotent.
   */
  markEngaged(subjectId: string): void {
    this.#autoStop.release(subjectId);
    /* And a ride waiting after backing up goes on when the card closes, as a
       stop does (ADR-0043). */
    this.#backingWatch.release();
  }

  /**
   * The domain finished a quest. Make the world show it.
   *
   * Inbound, because nothing in an adapter knows what a quest is: the scene is
   * told, and what it owns is the world's half — the subject stops asking to be
   * tapped — and the ordering, which the probe's event trace records so
   * `poi/engaged` before `quest/completed` is a fact a test can hold.
   *
   * An id nothing in this level matches is **still published**. The fact
   * happened; dropping it would be the silent-fallback defect again, so it is
   * logged and passed on rather than swallowed.
   */
  markCompleted(subjectId: string): void {
    if (this.#completed.has(subjectId)) return;
    const known = this.#reachTargets.some((target) => target.id === subjectId);
    if (!known) {
      console.error(
        `[level] a quest completed for "${subjectId}", which this level places no point of ` +
          `interest or character for. The milestone is still published; nothing in the world ` +
          `changes, because there is nothing to change.`,
      );
    }
    this.#completed.add(subjectId);
    this.#refreshAffordances();
    this.#emitMilestone('quest/completed', subjectId);
  }

  /**
   * The level's stamp was earned.
   *
   * Every remaining subject settles to `done`: the stamp is the level's, so
   * after it there is nothing left in this level asking to be done, and a ring
   * still pulsing at the player would be the game asking for something it has
   * already given them credit for. The screen that announces the stamp and the
   * level it opened is `app/ui`'s.
   *
   * Idempotent, because a stamp can only be earned once and a repeated call is a
   * bootstrap wiring mistake rather than a second event.
   */
  markLevelComplete(): void {
    if (this.#levelComplete) return;
    this.#levelComplete = true;
    for (const target of this.#reachTargets) this.#completed.add(target.id);
    this.#refreshAffordances();
    this.#emitMilestone('level/completed', this.#options.level.id);
  }

  #emitMilestone(name: SceneMilestoneName, detail: string): void {
    this.#options.probe?.recordEvent(name, detail);
    this.#options.onMilestone?.(name, detail);
  }

  /* -------------------------------------------------------- time of day --- */

  /**
   * Re-read the device clock every few seconds and let the sky follow it.
   *
   * A timer rather than per-frame work: the phase moves by 6e-5 of a day in five
   * seconds, most refreshes produce the same 8-bit colours, and a refresh that
   * changes nothing costs five blends and a string compare. Nothing here touches
   * a texture, so the decoded-texture budget is unaffected, and nothing here adds
   * a draw, so the tier is measuring the same frame it measured before.
   */
  #startSkyClock(): void {
    this.#skyClock = this.time.addEvent({
      delay: PHASE_REFRESH_MS,
      loop: true,
      callback: this.#tickSky,
    });
  }

  readonly #tickSky = (): void => {
    const target = dayPhase(this.#clock());
    const next = easePhase(this.#phase, target, MAX_PHASE_STEP);
    if (next === this.#phase) return;
    this.#phase = next;

    const palette = tintPalette(this.#options.level.palette, { phase: next });
    if (
      palette.sky === this.#palette.sky &&
      palette.ground === this.#palette.ground &&
      palette.horizon === this.#palette.horizon
    ) {
      /* The phase moved and no channel did. Nothing to redraw, which is the
         common case: this is why the tint costs nothing to keep running. */
      this.#options.probe?.publish({ dayPhase: next });
      return;
    }

    this.#palette = palette;
    for (const draw of this.#repaintables) draw();
    /* The gradient, or a placeholder sky, may be what the first row shows. */
    this.#reportSkyBand();
    this.#options.probe?.publish({ dayPhase: next });
  };

  /* ----------------------------------------------------------------- snow --- */

  #buildSnow(): void {
    this.#snowGraphics = this.add.graphics().setScrollFactor(0, 0).setDepth(this.#depths.snow);
  }

  /**
   * Deterministic seeding, and only as many flakes as the tier allows.
   *
   * Seeded from the index rather than `Math.random` so a screenshot of the same
   * level at the same tier is the same picture, which is what makes task 1.18's
   * device screenshots comparable at all.
   */
  #seedFlakes(quantity: number): void {
    const { designWidth, designHeight } = this.#options;
    this.#flakes = Array.from({ length: quantity }, (_unused, index) => {
      const a = ((index * 9301 + 49297) % 233280) / 233280;
      const b = ((index * 4523 + 12345) % 65521) / 65521;
      return {
        x: a * designWidth,
        y: b * designHeight,
        radius: FLAKE_MIN_RADIUS + a * (FLAKE_MAX_RADIUS - FLAKE_MIN_RADIUS),
        speed: 60 + b * 120,
        drift: (a - 0.5) * 40,
      };
    });
  }

  #updateSnow(dtSeconds: number): void {
    const graphics = this.#snowGraphics;
    if (graphics === null || this.#flakes.length === 0) return;

    const { designWidth, designHeight } = this.#options;
    graphics.clear();
    graphics.fillStyle(0xffffff, 0.75);
    for (const flake of this.#flakes) {
      flake.y += flake.speed * dtSeconds;
      flake.x += flake.drift * dtSeconds;
      if (flake.y > designHeight) flake.y -= designHeight;
      if (flake.x < 0) flake.x += designWidth;
      if (flake.x > designWidth) flake.x -= designWidth;
      graphics.fillCircle(flake.x, flake.y, flake.radius);
    }
  }
}
